/**
 * ScheduleView — the day / week / month ROSTER surface.
 *
 * 2026-08-01, operator instruction: "day, week and month view should only be the
 * schedule. It should not show appointments or any other data." So this surface
 * now answers exactly one question — WHEN AM I WORKING — and nothing else:
 * the rostered shift, the breaks and lunch inside it, and time off.
 *
 * Deliberately NOT here any more (all preserved in
 * quarantine/2026-0801-schedule-roster-only/): appointment blocks and the
 * appointment detail drawer, the free-slot booking affordances and booking
 * sheet, walk-ins, claimable open shifts, appointment counts, and the month's
 * appointment-volume shading. Appointments live in My Day; open shifts have
 * their own tab on the parent screen.
 *
 * Owns: the view toolbar, the week hour grid, the month roster grid, the day
 * board (hour axis + roster column + selection panel). Does NOT own page chrome
 * — the parent supplies the header, the stat band and the time-off modal.
 *
 * 2026-08-04, operator instruction: "My availability should not be overlayed on
 * top of the schedules. It should update the colour of the shift and be part of
 * the schedule. All settings for My Availability should be inside of the slide
 * out for the shift settings, it can be a tab in there." So availability is not
 * a layer any more — the published shift block PAINTS ITSELF from the windows
 * covering it (one coloured segment per window, palette colour, dashed edge
 * where a window runs past the shift), and every availability setting lives on
 * the shift drawer's second tab. The floating band overlay and the corner
 * editor are gone.
 *
 * Data: getMyDay per visible date (the source of shift + breaks) and
 * getMySchedule for the week's hours/target, which the parent's band reads.
 * Honesty notes: WorkforceScheduleTimeSandbox.tsx.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { Drawer } from "../../../shell/Overlay";
import { toast } from "../../../shell/toast";
import {
  getMyDay, getMySchedule, createSwapRequest, createShift, updateShift,
  type MyDayResponse, type MyDayAppointment, type MyScheduleResponse, type MyTimeResponse, type TimeOffEntry, type WfShift,
  getMyAvailability, saveMyAvailability, type MyAvailability, type AvailabilityBlock,
} from "../wf-api";
import { hmToMin, hmLabel, mondayOf, addDaysIso, weekDates, dayLabel, pad2 } from "../wf-shared";
import { useWfResource } from "../useWorkforce";
import { useT } from "../../../lib/i18n";

export type Mode = "day" | "week" | "month";
const HOUR_H = 54;
const DAY_HOUR_H = 112; // day view: a 30-min break still reads at 56px
/** Operator, 2026-08-01: the calendar always spans 06:00 → 23:00 and scrolls
 *  inside its own box, rather than shrink-wrapping the roster. */
const DAY_START_MIN = 6 * 60;
const DAY_END_MIN = 23 * 60;
const GRID_MAX_H = 620;

export const durLabel = (mins: number) => (mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}` : `${mins}m`);
/** minutes-from-midnight → the wall-clock string the ops speak. */
export const hm = (min: number) => `${pad2(Math.floor(min / 60))}:${pad2(min % 60)}`;
const snap15 = (min: number) => Math.round(min / 15) * 15;
const inRange = (iso: string, from: string, to: string) => iso >= from && iso <= to;
export const monthBounds = (anchor: string) => {
  const y = Number(anchor.slice(0, 4)), m = Number(anchor.slice(5, 7));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { first: `${anchor.slice(0, 7)}-01`, last: `${anchor.slice(0, 7)}-${pad2(last)}`, y, m: m - 1, dim: last };
};

export interface ViewData {
  dates: string[];
  days: (MyDayResponse | null)[];
  sched: MyScheduleResponse | null;
  /** date → the rostered shift (id kept, so clicking a month day opens the SAME
   *  record the week view would open). */
  roster: Record<string, { id: string; start: string; end: string; breaks: Brk[] }>;
}

/** Day and week read getMyDay (the only op carrying breaks). Month reads the
 *  roster off getMySchedule for each week it covers — myTime.myShifts is a short
 *  upcoming window, so a month drawn from it reads "Not scheduled" all through. */
export async function fetchView(mode: Mode, anchor: string): Promise<ViewData> {
  const soft = <R,>(p: Promise<R>) => p.catch(() => null);
  if (mode === "month") {
    const { first, last } = monthBounds(anchor);
    const starts: string[] = [];
    for (let w = mondayOf(first); w <= last; w = addDaysIso(w, 7)) starts.push(w);
    const weeks = await Promise.all(starts.map((w) => soft(getMySchedule(w))));
    const roster: ViewData["roster"] = {};
    for (const wk of weeks) for (const s of wk?.shifts || []) roster[s.date] = { id: s.id, start: s.start, end: s.end, breaks: s.breaks || [] };
    return { dates: [], days: [], sched: null, roster };
  }
  const dates = mode === "week" ? weekDates(mondayOf(anchor)) : [anchor];
  const [days, sched] = await Promise.all([
    Promise.all(dates.map((d) => soft(getMyDay(d)))),
    soft(getMySchedule(mondayOf(anchor))),
  ]);
  return { dates, days, sched, roster: {} };
}

/* ── Small parts ─────────────────────────────────────────────────────── */

function Segmented({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  const t = useT();
  const label: Record<Mode, string> = {
    day: t("workforce.mySchedule.day", "Day"),
    week: t("workforce.mySchedule.week", "Week"),
    month: t("workforce.mySchedule.month", "Month"),
  };
  return (
    <div style={{ display: "flex", padding: 3, background: T.n6, borderRadius: 7, gap: 2 }}>
      {(["day", "week", "month"] as Mode[]).map((m) => (
        <button key={m} onClick={() => onChange(m)} data-testid={`mysched-mode-${m}`}
          style={{ border: "none", background: mode === m ? T.white : "transparent", color: mode === m ? T.n1 : T.soft, fontSize: 12.5, padding: "6px 18px", borderRadius: 5, cursor: "pointer", fontFamily: T.font, fontWeight: mode === m ? 700 : 500, boxShadow: mode === m ? T.shadowSm : "none" }}>
          {label[m]}
        </button>
      ))}
    </div>
  );
}

export function Stat({ label, value, sub, bar }: { label: string; value: string; sub?: string; bar?: { pct: number; tone: string } }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 96 }}>
      <span style={{ fontSize: 10.5, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 17, fontWeight: 700, color: T.n1, fontFamily: T.fontMono }}>{value}</span>
      {bar ? (
        <span style={{ display: "block", height: 4, borderRadius: 999, background: T.n6, overflow: "hidden", marginTop: 1 }}>
          <span style={{ display: "block", height: "100%", width: `${Math.min(100, bar.pct)}%`, background: bar.tone, borderRadius: 999 }} />
        </span>
      ) : null}
      {sub ? <span style={{ fontSize: 11, color: T.soft }}>{sub}</span> : null}
    </div>
  );
}

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span style={{ fontSize: 10, fontWeight: 700, color: tone, background: alpha(tone, 12), padding: "2px 7px", borderRadius: 999, whiteSpace: "nowrap" }}>{children}</span>;
}

/** The shift window as a chip — how the day header and month cells state "you
 *  are rostered". Compact by design: it has to survive a 94px month cell, so no
 *  spaces around the dash, and it clips rather than spilling past its column. */
function ShiftChip({ start, end }: { start: string; end: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", alignSelf: "flex-start", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", fontSize: 10.5, fontWeight: 700, fontFamily: T.fontMono, color: T.p1, background: alpha(T.p1, 10), border: `1px solid ${alpha(T.p1, 28)}`, borderRadius: 999, padding: "1px 7px", whiteSpace: "nowrap" }}>
      {hmLabel(start)}–{hmLabel(end)}
    </span>
  );
}

const brkText = (kind: "break" | "lunch") => (kind === "lunch"
  ? { labelKey: "workforce.mySchedule.lunch", label: "Lunch" }
  : { labelKey: "workforce.mySchedule.break", label: "Break" });
const HATCH = `repeating-linear-gradient(45deg, ${alpha(T.n4, 24)} 0 5px, ${T.white} 5px 11px)`;
/** Hard spaces inside a wall-clock time so "8 AM–4 PM" can only ever break AFTER
 *  the dash — a week column is ~80px and hmLabel renders 12-hour times. */
const nb = (s: string) => s.replace(/ /g, "\u00a0");
/* ── Appointment layer (2026-08-01, operator): OFF by default, toggled on in the
 * toolbar. A read-only visibility layer — appointments are still worked in My
 * Day; this only answers "what is booked into these hours". ── */
const APPT_SPINE = 20;
const CH: Record<MyDayAppointment["method"], { color: string; icon: "users" | "phone" | "video"; label: string; labelKey: string }> = {
  "in-person": { color: T.tDarkBlue, icon: "users", label: "In person", labelKey: "workforce.mySchedule.inPerson" },
  phone: { color: T.tTurquoise, icon: "phone", label: "Phone", labelKey: "workforce.mySchedule.phone" },
  video: { color: T.tPurple, icon: "video", label: "Video", labelKey: "workforce.mySchedule.video" },
};
interface Span { start: number; end: number }
interface ApptSpan extends Span { appt: MyDayAppointment }
interface Placed<X> { item: X; lane: number; lanes: number }
const laneStyle = (lane: number, lanes: number): React.CSSProperties => {
  const w = 100 / lanes;
  return { left: `calc(${lane * w}% + 2px)`, width: `calc(${w}% - 4px)` };
};
/** Greedy lane packing so overlapping appointments sit side by side. */
function packLanes<X extends Span>(items: X[]): Placed<X>[] {
  const sorted = [...items].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: Placed<X>[] = [];
  let cluster: { item: X; lane: number }[] = [];
  let clusterEnd = -1;
  let laneEnds: number[] = [];
  const flush = () => {
    if (!cluster.length) return;
    const lanes = cluster.reduce((m, c) => Math.max(m, c.lane + 1), 1);
    for (const c of cluster) out.push({ item: c.item, lane: c.lane, lanes });
    cluster = []; laneEnds = [];
  };
  for (const it of sorted) {
    if (cluster.length && it.start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((e) => e <= it.start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); } else { laneEnds[lane] = it.end; }
    cluster.push({ item: it, lane });
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  flush();
  return out;
}
function ApptBlock({ a, lane, lanes, top, height, wide }: { a: MyDayAppointment; lane: number; lanes: number; top: number; height: number; wide: boolean }) {
  const ch = CH[a.method];
  const done = a.status === "completed";
  const missed = a.status === "no-show";
  return (
    <div data-testid="mysched-appt" data-id={a.id} title={`${hmLabel(a.start)}–${hmLabel(a.end)} · ${a.customer} · ${a.service}`}
      style={{ position: "absolute", top, height: Math.max(16, height - 2), ...laneStyle(lane, lanes), padding: wide ? "4px 8px" : "2px 5px", borderRadius: 6, overflow: "hidden",
        background: missed ? alpha(T.red1, 7) : done ? T.n6 : alpha(ch.color, 10),
        border: `1px ${missed ? "dashed" : "solid"} ${missed ? alpha(T.red1, 45) : alpha(ch.color, 30)}`,
        borderLeft: `3px solid ${missed ? T.red1 : done ? T.n4 : ch.color}`,
        fontFamily: T.font, display: "flex", flexDirection: "column", gap: 1, opacity: done ? 0.75 : 1 }}>
      <span style={{ fontSize: 9.5, color: T.soft, fontFamily: T.fontMono, lineHeight: 1.2 }}>{nb(hmLabel(a.start))}</span>
      <span style={{ fontSize: wide ? 12 : 10.5, fontWeight: 700, color: T.n1, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: missed ? "line-through" : "none" }}>{a.customer}</span>
      {wide && height >= 52 ? <span style={{ fontSize: 11, color: T.n2, lineHeight: 1.3, overflow: "hidden" }}>{a.serviceShort || a.service}</span> : null}
    </div>
  );
}
const apptSpans = (day: MyDayResponse | null): ApptSpan[] =>
  (day?.appointments || []).map((a) => ({ appt: a, start: hmToMin(a.start), end: hmToMin(a.end) }));
const RosterTime = ({ start, end, size = 10.5 }: { start: string; end: string; size?: number }) => (
  <span style={{ fontSize: size, fontWeight: 700, color: T.p1, fontFamily: T.fontMono, lineHeight: 1.25, minWidth: 0 }}>
    {nb(hmLabel(start))}–{nb(hmLabel(end))}
  </span>
);

export function netWorkedMins(shift: NonNullable<MyDayResponse["shift"]>) {
  return (hmToMin(shift.end) - hmToMin(shift.start)) - shift.breaks.reduce((s, b) => s + (hmToMin(b.end) - hmToMin(b.start)), 0);
}
/** Net rostered minutes for a month cell (same arithmetic, looser input). */
const netOf = (s: { start: string; end: string; breaks: Brk[] }) =>
  Math.max(0, (hmToMin(s.end) - hmToMin(s.start)) - s.breaks.reduce((m, b) => m + Math.max(0, hmToMin(b.end) - hmToMin(b.start)), 0));
/** vacation/personal/other read as time off; sick is called out separately. */
const isSick = (e: TimeOffEntry) => e.type === "Sick";
type MyShift = NonNullable<MyDayResponse["shift"]>;
type Brk = { start: string; end: string; kind: "break" | "lunch" };
/** One day the add-shift drawer can write to, with what is already on it.
 *  `off: "approved"` can't be rostered over (operator, 2026-08-01). */
export type DayState = { iso: string; shift: MyShift | null; off: "approved" | "pending" | null };
type Fix = "skip" | "replace" | "merge";
/** What a create needs that MyDayResponse.shift doesn't carry (staffId, role,
 *  locationId). Borrowed from the banker's OWN roster in getMySchedule — never
 *  invented; with no shift anywhere in the week response a create can't be sent. */
export type ShiftTemplate = Pick<WfShift, "staffId" | "role" | "locationId"> | null;

/** What the banker has picked on the day board. `range` is a drag on the time
 *  axis; the other two are real records the response gave us. */
export type DaySel =
  | { kind: "shift" }
  | { kind: "brk"; i: number }
  | { kind: "range"; start: number; end: number };

const selEq = (a: DaySel | null, b: DaySel): boolean => {
  if (!a || a.kind !== b.kind) return false;
  if (a.kind === "shift") return true;
  if (a.kind === "brk" && b.kind === "brk") return a.i === b.i;
  if (a.kind === "range" && b.kind === "range") return a.start === b.start && a.end === b.end;
  return false;
};
const ring = (on: boolean): React.CSSProperties => (on ? { boxShadow: `0 0 0 2px ${T.p1}`, zIndex: 4 } : {});

/* ── Availability, drawn AS the shift (operator, 2026-08-04) ───────────────
 * Not a band over the roster: the advisor's own pattern is what gives the published shift its
 * colour. Each window covering the shift paints its slice of the block, in the document palette
 * (the same colour means the same thing on every surface). The uncoloured remainder of a shift is
 * honest too — on shift, offering nothing. Editing lives on the shift drawer's Availability tab. ── */
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function dowOf(iso: string): string { return DOW[new Date(iso + "T00:00:00").getDay()]; }
/** "Monday" — availability is a WEEKLY pattern, so its editor names the weekday, never the date. */
const weekdayName = (iso: string) => new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(new Date(iso + "T00:00:00"));
const paletteHex = (palette: { id: string; name: string; hex: string }[], id?: string | null) =>
  palette.find((c) => c.id === (id || "slate"))?.hex ?? T.soft;

export type AvSeg = { block: AvailabilityBlock; from: number; to: number; overruns: boolean };
/** The availability windows covering a shift, clipped to it — so a shift block can render itself as
 *  named, coloured segments rather than carrying a separate overlay. */
function availSegments(date: string, blocks: AvailabilityBlock[], shift: { start: string; end: string } | null): AvSeg[] {
  if (!shift) return [];
  const day = dowOf(date);
  const s0 = hmToMin(shift.start), s1 = hmToMin(shift.end);
  return blocks
    .filter((b) => b.day === day)
    .map((b) => {
      const a = Math.max(s0, hmToMin(b.start)), z = Math.min(s1, hmToMin(b.end));
      return z > a ? { block: b, from: a, to: z, overruns: hmToMin(b.start) < s0 || hmToMin(b.end) > s1 } : null;
    })
    .filter(Boolean)
    .sort((x, y) => (x as AvSeg).from - (y as AvSeg).from) as AvSeg[];
}
const segsFor = (date: string, avail: MyAvailability | null, shift: { start: string; end: string } | null) =>
  avail ? availSegments(date, avail.blocks, shift) : [];
/** One colour when the whole shift is one window's, otherwise null — the frame stays neutral so no
 *  single window can claim hours it doesn't cover. */
const soloHex = (segs: AvSeg[], palette: { id: string; name: string; hex: string }[]) => {
  if (!segs.length) return null;
  const hexes = new Set(segs.map((s) => paletteHex(palette, s.block.color)));
  return hexes.size === 1 ? [...hexes][0] : null;
};
/** Frame + fill for a shift block, given what the advisor offers inside it. */
function shiftSkin(segs: AvSeg[], palette: { id: string; name: string; hex: string }[], strong: boolean) {
  /* One window owning the whole shift recolours it outright; a mixed day keeps the roster's own
     frame and lets the segments inside carry the colours. Same rule in day and week. */
  const tint = soloHex(segs, palette) ?? T.p1;
  return { accent: tint, background: alpha(tint, strong ? 8 : 10), border: `1px solid ${alpha(tint, strong ? 80 : 30)}`, borderLeft: `3px solid ${tint}` };
}
const segTitle = (segs: AvSeg[]) => segs.map((s) => s.block.name).filter(Boolean).join(" · ");
/** One window's full details, for the hover title: what it is, when, how it can be reached, what can
 *  be booked in it, and whether it overruns the shift. The operator asked to see methods and services
 *  on hover, so they belong in the tooltip text rather than only inside the editor. */
function windowTitle(seg: AvSeg): string {
  const b = seg.block;
  const methods = [b.methods.inPerson ? "In person" : null, b.methods.phone ? "Phone" : null, b.methods.video ? "Virtual" : null].filter(Boolean).join(", ") || "No method set";
  const services = b.allServices
    ? "All my services"
    : (b.offers.filter((o) => o.bookable).map((o) => o.name).join(", ") || "Nothing bookable on these methods");
  const lines = [
    `${b.name || "Available"} · ${hmLabel(b.start)}–${hmLabel(b.end)}`,
    `Methods: ${methods}`,
    `Services: ${services}`,
  ];
  if (seg.overruns) lines.push("Outside your shift — only the overlap is bookable.");
  const blocked = b.offers.filter((o) => !o.bookable).length;
  if (blocked) lines.push(`${blocked} service${blocked === 1 ? "" : "s"} can't be offered on these methods.`);
  if (b.note) lines.push(b.note);
  return lines.join("\n");
}

/** The coloured slices themselves. Painted at z-0 inside the block so breaks, the now-line and any
 *  in-flow shift label still sit above them. */
function ShiftFill({ segs, palette, top0, y, labels }: {
  segs: AvSeg[]; palette: { id: string; name: string; hex: string }[];
  top0: number; y: (m: number) => number; labels: "none" | "left" | "right";
}) {
  return (
    <>
      {segs.map((s) => {
        const c = paletteHex(palette, s.block.color);
        const top = y(s.from) - top0;
        const h = Math.max(4, y(s.to) - y(s.from));
        const cutTop = hmToMin(s.block.start) < s.from;
        const cutEnd = hmToMin(s.block.end) > s.to;
        return (
          <span key={s.block.id} data-testid="sched-avail-seg" data-id={s.block.id}
            title={windowTitle(s)}
            style={{ position: "absolute", left: 0, right: 0, top, height: h, zIndex: 0, overflow: "hidden",
              background: alpha(c, 16), borderLeft: `3px solid ${c}`,
              borderTop: cutTop ? `2px dashed ${alpha(c, 55)}` : "none",
              borderBottom: cutEnd ? `2px dashed ${alpha(c, 55)}` : "none",
              pointerEvents: "none" }}>
            {labels !== "none" && h >= 17 ? (
              /* The one hoverable part of the segment: the body stays click-through so the shift
                 block keeps click-to-edit and empty-day drag-to-create. */
              <span title={windowTitle(s)}
                style={{ position: "absolute", top: 2, left: labels === "left" ? 7 : 6, right: 6, fontSize: 9.5, fontWeight: 700, color: c,
                textAlign: labels === "right" ? "right" : "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                pointerEvents: "auto", cursor: "help" }}>
                {s.block.name}
              </span>
            ) : null}
            {labels === "left" && h >= 32 ? (
              <span style={{ position: "absolute", top: 15, left: 7, right: 5, fontSize: 9, fontFamily: T.fontMono, color: alpha(c, 75), overflow: "hidden", whiteSpace: "nowrap" }}>
                {nb(hmLabel(hm(s.from)))}–{nb(hmLabel(hm(s.to)))}
              </span>
            ) : null}
          </span>
        );
      })}
    </>
  );
}

/* ── Day board ───────────────────────────────────────────────────────── */

/** The roster column: the shift, with its breaks laid on top. Selectable. */
function RosterColumn({ day, off, lo, minH, sel, onSelect, onEdit, segs, palette }: {
  day: MyDayResponse | null; off: TimeOffEntry | undefined; lo: number; minH: number;
  sel: DaySel | null; onSelect: (s: DaySel) => void; onEdit: () => void;
  segs: AvSeg[]; palette: { id: string; name: string; hex: string }[];
}) {
  const t = useT();
  const shift = day?.shift || null;
  const y = (m: number) => (m - lo) * minH;
  if (!shift) {
    return (
      <div style={{ position: "absolute", inset: "18px 12px auto", textAlign: "center" }}>
        <span style={{ fontSize: 12.5, color: T.soft }}>
          {off ? `${off.type} · ${off.status}` : t("workforce.mySchedule.notScheduledLong", "No shift scheduled for this day.")}
        </span>
      </div>
    );
  }
  const top = y(hmToMin(shift.start));
  const h = y(hmToMin(shift.end)) - top;
  const on = selEq(sel, { kind: "shift" });
  const skin = shiftSkin(segs, palette, on);
  const named = segTitle(segs);
  const lift: React.CSSProperties = { position: "relative", zIndex: 1 };
  return (
    <>
      <button onClick={() => { onSelect({ kind: "shift" }); onEdit(); }} data-testid="mysched-shift-block" data-id={shift.id}
        title={`${t("workforce.mySchedule.editShift", "Edit shift")} · ${hmLabel(shift.start)}–${hmLabel(shift.end)}${named ? ` · ${named}` : ""}`}
        style={{ position: "absolute", left: 10, right: 10, top, height: Math.max(30, h), borderRadius: 9, cursor: "pointer",
          overflow: "hidden", textAlign: "left", fontFamily: T.font, background: skin.background,
          border: skin.border, borderLeft: skin.borderLeft,
          padding: "10px 13px", display: "flex", flexDirection: "column", gap: 4, ...ring(on) }}>
        <ShiftFill segs={segs} palette={palette} top0={top} y={y} labels="right" />
        <span style={{ ...lift, fontSize: 10, fontWeight: 700, color: skin.accent, textTransform: "uppercase", letterSpacing: 0.06 }}>{t("workforce.mySchedule.onShift", "On shift")}</span>
        <span style={{ ...lift, fontSize: 15, fontWeight: 700, color: T.n1, fontFamily: T.fontMono, whiteSpace: "nowrap" }}>{hmLabel(shift.start)} – {hmLabel(shift.end)}</span>
        {h >= 110 ? <span style={{ ...lift, fontSize: 12, color: T.soft }}>{durLabel(netWorkedMins(shift))} {t("workforce.mySchedule.net", "net")}</span> : null}
        {h >= 150 ? <span style={{ ...lift, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: T.p1 }}><Icon.edit size={11} /> {t("workforce.mySchedule.clickToEdit", "Click to edit")}</span> : null}
      </button>
      {shift.breaks.map((b, i) => {
        const bt = y(hmToMin(b.start)), bh = y(hmToMin(b.end)) - bt;
        const bon = selEq(sel, { kind: "brk", i });
        const txt = brkText(b.kind);
        return (
          <button key={i} onClick={() => onSelect({ kind: "brk", i })} data-testid="mysched-break-block" data-id={String(i)}
            title={`${t(txt.labelKey, txt.label)} ${hmLabel(b.start)}–${hmLabel(b.end)}`}
            style={{ position: "absolute", left: 22, right: 22, top: bt, height: Math.max(18, bh), borderRadius: 7, cursor: "pointer",
              zIndex: 3, overflow: "hidden", textAlign: "left", fontFamily: T.font, background: HATCH,
              border: `1px ${bon ? "solid" : "dashed"} ${bon ? T.p1 : T.n5}`, padding: "3px 11px",
              display: "flex", alignItems: "center", gap: 10, ...ring(bon) }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.n2, whiteSpace: "nowrap" }}>{t(txt.labelKey, txt.label)}</span>
            {bh >= 30 ? <span style={{ fontSize: 11.5, color: T.n1, fontFamily: T.fontMono, whiteSpace: "nowrap" }}>{hmLabel(b.start)} – {hmLabel(b.end)}</span> : null}
          </button>
        );
      })}
    </>
  );
}

/** Drag/selection band — drawn over a column, never intercepting clicks. */
function RangeBand({ start, end, lo, minH, label }: { start: number; end: number; lo: number; minH: number; label?: string }) {
  const h = Math.max(3, (end - start) * minH);
  return (
    <div aria-hidden data-testid="mysched-range" style={{ position: "absolute", left: 2, right: 2, top: (start - lo) * minH, height: h, background: alpha(T.p1, 12), border: `1.5px dashed ${T.p1}`, borderRadius: 7, pointerEvents: "none", zIndex: 6, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
      {label && h >= 24 ? <span style={{ marginTop: 3, fontSize: 10.5, fontWeight: 700, color: T.p1, background: T.white, border: `1px solid ${alpha(T.p1, 35)}`, borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" }}>{label}</span> : null}
    </div>
  );
}

/**
 * Everything you can do to the selected part of the roster. Real ops are
 * buttons; the things no op exists for (moving a break, asking for different
 * hours) are visibly disabled against REQ-123 rather than faked.
 */
function SelectionPanel({ day, sel, isToday, busy, onSwap, onTimeOff, onReportSick, onEdit, onBlockRange, onClear }: {
  day: MyDayResponse | null; sel: DaySel | null; isToday: boolean; busy: string | null;
  onSwap: () => void; onTimeOff: () => void; onReportSick: () => void; onEdit: () => void; onBlockRange: () => void; onClear: () => void;
}) {
  const t = useT();
  const shift = day?.shift || null;
  const brkSel = sel && sel.kind === "brk" ? sel : null;
  const rangeSel = sel && sel.kind === "range" ? sel : null;
  const brk = brkSel && shift ? shift.breaks[brkSel.i] ?? null : null;
  const card: React.CSSProperties = { border: `1px solid ${sel ? alpha(T.p1, 45) : T.border}`, borderRadius: 12, background: T.white, padding: "13px 15px", display: "flex", flexDirection: "column", gap: 10, boxShadow: sel ? `0 0 0 3px ${T.p4}` : "none" };
  const head: React.CSSProperties = { fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 };
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
      <span style={{ color: T.n2 }}>{label}</span>
      <span style={{ fontWeight: 700, color: T.n1, fontFamily: T.fontMono }}>{value}</span>
    </div>
  );

  if (!sel) {
    return (
      <div style={card} data-testid="mysched-selection">
        <span style={head}>{t("workforce.mySchedule.selection", "Selection")}</span>
        <span style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>
          {t("workforce.mySchedule.selectHintRoster", "Click your shift or a break to act on it — or drag down the time axis to select a range.")}
        </span>
      </div>
    );
  }

  const title = sel.kind === "shift" ? t("workforce.mySchedule.onShift", "On shift")
    : brk ? t(brkText(brk.kind).labelKey, brkText(brk.kind).label)
    : t("workforce.mySchedule.selectedRange", "Selected time");

  return (
    <div style={card} data-testid="mysched-selection">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={head}>{title}</span>
        <button onClick={onClear} data-testid="mysched-selection-clear" style={{ marginLeft: "auto", border: "none", background: "transparent", color: T.soft, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{t("workforce.mySchedule.clear", "Clear")}</button>
      </div>

      {sel.kind === "shift" && shift ? (
        <>
          <Row label={t("workforce.mySchedule.start", "Start")} value={hmLabel(shift.start)} />
          <Row label={t("workforce.mySchedule.end", "End")} value={hmLabel(shift.end)} />
          <Row label={t("workforce.mySchedule.netHours", "Net hours")} value={durLabel(netWorkedMins(shift))} />
          <Row label={t("workforce.mySchedule.breaksCount", "Breaks")} value={String(shift.breaks.length)} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: `1px solid ${T.n6}`, paddingTop: 10 }}>
            <Btn size="sm" style={{ justifyContent: "center" }} onClick={onEdit}>
              <Icon.edit size={12} /> {t("workforce.mySchedule.editShift", "Edit shift")}
            </Btn>
            <Btn variant="secondary" size="sm" style={{ justifyContent: "center" }} disabled={busy !== null} onClick={onSwap}>
              <Icon.refresh size={12} /> {busy === "swap" ? "…" : t("workforce.mySchedule.swapThisShift", "Offer this shift for swap")}
            </Btn>
            <Btn variant="secondary" size="sm" style={{ justifyContent: "center" }} onClick={onTimeOff}>
              <Icon.calendar size={12} /> {t("workforce.mySchedule.requestTimeOffFrom", "Request time off from here")}
            </Btn>
            {isToday ? (
              <Btn variant="secondary" size="sm" style={{ justifyContent: "center", color: T.red1 }} onClick={onReportSick}>
                <Icon.warn size={12} /> {t("workforce.mySchedule.reportSick", "Report sick")}
              </Btn>
            ) : null}
          </div>
        </>
      ) : null}

      {brk ? (
        <>
          <Row label={t("workforce.mySchedule.start", "Start")} value={hmLabel(brk.start)} />
          <Row label={t("workforce.mySchedule.end", "End")} value={hmLabel(brk.end)} />
          <Row label={t("workforce.mySchedule.length", "Length")} value={durLabel(hmToMin(brk.end) - hmToMin(brk.start))} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: `1px solid ${T.n6}`, paddingTop: 10 }}>
            <Btn size="sm" style={{ justifyContent: "center" }} onClick={onEdit}>
              <Icon.edit size={12} /> {t("workforce.mySchedule.moveThisBreak", "Move this break")}
            </Btn>
          </div>
        </>
      ) : null}

      {rangeSel ? (
        <>
          <Row label={t("workforce.mySchedule.from", "From")} value={hmLabel(hm(rangeSel.start))} />
          <Row label={t("workforce.mySchedule.to", "To")} value={hmLabel(hm(rangeSel.end))} />
          <Row label={t("workforce.mySchedule.length", "Length")} value={durLabel(rangeSel.end - rangeSel.start)} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: `1px solid ${T.n6}`, paddingTop: 10 }}>
            <Btn size="sm" style={{ justifyContent: "center" }} onClick={onBlockRange}>
              <Icon.plus size={12} /> {t("workforce.mySchedule.blockThisTime", "Block this time out")}
            </Btn>
            <Btn variant="secondary" size="sm" style={{ justifyContent: "center" }} onClick={onTimeOff}>
              <Icon.calendar size={12} /> {t("workforce.mySchedule.requestTimeOffDay", "Request time off (whole day)")}
            </Btn>
          </div>
        </>
      ) : null}

      <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.5, borderTop: `1px solid ${T.n6}`, paddingTop: 9 }}>
        {t("workforce.mySchedule.scheduleYoursNote", "Changes you save here go straight to the branch schedule, so your manager sees them. Swaps, time off and sick days still go as requests.")}
      </span>
    </div>
  );
}

/* ── My availability, on the shift drawer's second tab (operator, 2026-08-04) ──
 * "All settings for My Availability should be inside of the slide out for the shift settings, it
 * can be a tab in there to make those changes."
 *
 * A window is a WEEKDAY pattern, so what is edited here applies to every Monday, not just this
 * date — said plainly at the top rather than left to be discovered. Times are bounded by the
 * published shift on the first tab: a window that reaches past it is trimmed to the overlap on
 * save, because that is all the engine will ever serve as bookable. ── */
const availKey = (bs: AvailabilityBlock[]) => JSON.stringify(bs.map((b) => ({
  i: b.id, s: b.start, e: b.end, n: (b.name ?? "").trim(), c: b.color ?? "slate",
  m: b.methods, v: [...b.services].sort(), o: b.note ?? "",
})));
const blankWindow = (day: string, start: string, end: string): AvailabilityBlock => ({
  id: `local:${Math.random().toString(36).slice(2, 9)}`, day, start, end,
  name: "", color: "slate", methods: { inPerson: true, phone: false, video: false },
  services: [], note: null, minutes: Math.max(0, hmToMin(end) - hmToMin(start)),
  allServices: true, offers: [], bookableCount: 0, blockedCount: 0,
});

function AvailabilityTab({ apiDay, dayName, shift, wins, setWins, palette, catalogue, problem }: {
  apiDay: string; dayName: string;
  shift: { start: string; end: string } | null;
  wins: AvailabilityBlock[];
  setWins: React.Dispatch<React.SetStateAction<AvailabilityBlock[]>>;
  palette: { id: string; name: string; hex: string }[];
  catalogue: MyAvailability["catalogue"];
  problem: string | null;
}) {
  const t = useT();
  const card: React.CSSProperties = { background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: "13px 15px", display: "flex", flexDirection: "column", gap: 11 };
  const head: React.CSSProperties = { fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 };
  const timeInput: React.CSSProperties = { height: 32, padding: "0 8px", border: `1px solid ${T.n5}`, borderRadius: 8, background: T.white, fontFamily: T.fontMono, fontSize: 12.5, color: T.n1, minWidth: 0, flex: "1 1 96px" };
  const patch = (i: number, p: Partial<AvailabilityBlock>) => setWins((cur) => cur.map((w, j) => (j === i ? { ...w, ...p } : w)));
  /** The same intersection the engine does — a branch-only service can't be offered by phone,
   *  whatever the window claims — so the count under each window is the real one, not a guess. */
  const reachOf = (w: AvailabilityBlock) => {
    const chosen = w.services.length ? catalogue.filter((c) => w.services.includes(c.id)) : catalogue;
    const ok = chosen.filter((c) => (w.methods.inPerson && c.channels.inPerson) || (w.methods.phone && c.channels.phone) || (w.methods.video && c.channels.video));
    return { total: chosen.length, bookable: ok.length };
  };
  /** The first uncovered stretch of the shift, so "Add a window" can never mint a zero-length row
   *  on a day whose windows already reach the end. Null when there is no usable gap left. */
  const freeSlot = (() => {
    const lo = hmToMin(shift?.start ?? "09:00"), hi = hmToMin(shift?.end ?? "17:00");
    const taken = wins
      .map((w) => ({ a: Math.max(lo, hmToMin(w.start)), z: Math.min(hi, hmToMin(w.end)) }))
      .filter((r) => r.z > r.a)
      .sort((x, y) => x.a - y.a);
    let cur = lo;
    for (const r of taken) {
      if (r.a - cur >= 15) break;
      cur = Math.max(cur, r.z);
    }
    if (hi - cur < 15) return null;
    const next = taken.find((r) => r.a >= cur);
    const end = Math.min(next ? next.a : hi, cur + 120);
    return { start: hm(cur), end: hm(end) };
  })();
  const addWindow = () => { if (freeSlot) setWins((cur) => [...cur, blankWindow(apiDay, freeSlot.start, freeSlot.end)]); };

  return (
    <>
      <div style={{ ...card, background: T.p4, border: `1px solid ${alpha(T.p1, 25)}` }}>
        <span style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
          {t("workforce.mySchedule.availWeekly", "These windows are your weekly pattern — what you change here applies to every")}{" "}
          <b>{dayName}</b>.{" "}
          {shift
            ? <>{t("workforce.mySchedule.availBounded", "Only the hours inside your shift")} <b style={{ fontFamily: T.fontMono }}>{hmLabel(shift.start)}–{hmLabel(shift.end)}</b> {t("workforce.mySchedule.availBounded2", "can be booked.")}</>
            : t("workforce.mySchedule.noShiftThatDay", "No published shift that day — nothing here is bookable until one exists.")}
        </span>
      </div>

      {wins.length === 0 ? (
        <div style={{ ...card, alignItems: "flex-start", gap: 8 }}>
          <span style={head}>{t("workforce.mySchedule.noWindows", "No windows on this day")}</span>
          <span style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{t("workforce.mySchedule.noWindowsBody", "You're rostered, but nothing is offered — the shift shows uncoloured on the schedule and nothing can be booked into it.")}</span>
        </div>
      ) : null}

      {wins.map((w, i) => {
        const c = paletteHex(palette, w.color);
        const reach = reachOf(w);
        const outside = !!shift && (w.start < shift.start || w.end > shift.end);
        return (
          <div key={w.id} data-testid="mysched-avail-window" data-id={w.id} style={{ ...card, borderLeft: `3px solid ${c}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input value={w.name ?? ""} onChange={(e) => patch(i, { name: e.target.value })} data-testid="mysched-avail-name"
                placeholder={t("workforce.mySchedule.nameThisWindow", "Name this window")}
                style={{ flex: 1, minWidth: 0, height: 32, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.n5}`, fontSize: 13, fontFamily: T.font, fontWeight: 700, color: T.n1 }} />
              <button onClick={() => setWins((cur) => cur.filter((_, j) => j !== i))} data-testid="mysched-avail-remove"
                aria-label={t("common.actions.remove", "Remove")}
                style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${T.n6}`, background: T.white, color: T.soft, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon.trash size={12} />
              </button>
            </div>

            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {palette.map((p) => (
                <button key={p.id} type="button" data-testid="mysched-avail-color" data-id={p.id} aria-label={p.name} title={p.name}
                  onClick={() => patch(i, { color: p.id })}
                  style={{ width: 22, height: 22, borderRadius: 6, background: p.hex, cursor: "pointer", padding: 0,
                    border: (w.color || "slate") === p.id ? `2px solid ${T.n1}` : `1px solid ${alpha(T.n1, 20)}` }} />
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="time" step={900} value={w.start} min={shift?.start} max={shift?.end} data-testid="mysched-avail-start"
                aria-label={t("workforce.mySchedule.startTime", "Start time")}
                onChange={(e) => patch(i, { start: e.target.value })} style={timeInput} />
              <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.mySchedule.to", "to")}</span>
              <input type="time" step={900} value={w.end} min={shift?.start} max={shift?.end} data-testid="mysched-avail-end"
                aria-label={t("workforce.mySchedule.endTime", "End time")}
                onChange={(e) => patch(i, { end: e.target.value })} style={timeInput} />
            </div>
            {outside && shift ? (
              <span style={{ fontSize: 11.5, color: T.orange1, lineHeight: 1.45 }}>
                {t("workforce.mySchedule.outsideShift", "That's outside your shift")} ({hmLabel(shift.start)}–{hmLabel(shift.end)}) — {t("workforce.mySchedule.willBeTrimmed", "it will be trimmed to the overlap when you save.")}
              </span>
            ) : null}

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={head}>{t("workforce.mySchedule.methodsLabel", "Methods")}</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {([["inPerson", t("workforce.mySchedule.inPerson", "In person")], ["phone", t("workforce.mySchedule.phone", "Phone")], ["video", t("workforce.mySchedule.virtual", "Virtual")]] as const).map(([k, label]) => {
                  const on = w.methods[k];
                  return (
                    <button key={k} type="button" data-testid="mysched-avail-method" data-id={k} aria-pressed={on}
                      onClick={() => patch(i, { methods: { ...w.methods, [k]: !w.methods[k] } })}
                      style={{ padding: "5px 11px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : T.n2, fontSize: 12, fontWeight: on ? 700 : 600, fontFamily: T.font, cursor: "pointer" }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={head}>{t("workforce.mySchedule.whatCanBeBooked", "What can be booked here")}</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 148, overflow: "auto" }}>
                {catalogue.map((s) => {
                  const on = w.services.includes(s.id);
                  const reachable = (w.methods.inPerson && s.channels.inPerson) || (w.methods.phone && s.channels.phone) || (w.methods.video && s.channels.video);
                  return (
                    <button key={s.id} type="button" data-testid="mysched-avail-service" data-id={s.id} aria-pressed={on}
                      title={reachable ? s.name : t("workforce.mySchedule.notReachable", "Not offered on any method this window allows")}
                      onClick={() => patch(i, { services: on ? w.services.filter((x) => x !== s.id) : [...w.services, s.id] })}
                      style={{ padding: "4px 9px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? alpha(T.p1, 10) : T.white, color: on ? T.p1 : reachable ? T.n2 : T.soft, fontSize: 11.5, fontWeight: on ? 700 : 500, fontFamily: T.font, cursor: "pointer", opacity: reachable ? 1 : 0.6 }}>
                      {s.name}{reachable ? "" : " ⚠"}
                    </button>
                  );
                })}
              </div>
              <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>
                {w.services.length === 0 ? t("workforce.mySchedule.allHint", "Nothing selected means everything in your line of business.") : null}
                {w.services.length === 0 ? " " : ""}
                {reach.bookable} {t("workforce.mySchedule.bookableHere", "bookable in this window")}
                {reach.total - reach.bookable > 0 ? ` · ${reach.total - reach.bookable} ${t("workforce.mySchedule.cantBeOfferedHere", "can't be offered on these methods")}` : ""}
              </span>
            </div>

            <input value={w.note ?? ""} onChange={(e) => patch(i, { note: e.target.value })} data-testid="mysched-avail-note"
              placeholder={t("workforce.mySchedule.noteOptional", "Note (optional) — shows when you hover the shift")}
              style={{ height: 32, padding: "0 10px", borderRadius: 8, border: `1px solid ${T.n5}`, fontSize: 12.5, fontFamily: T.font, color: T.n1 }} />
          </div>
        );
      })}

      <button onClick={addWindow} data-testid="mysched-avail-add" disabled={!freeSlot}
        title={freeSlot ? undefined : t("workforce.mySchedule.noRoomLeft", "Your windows already cover the whole shift — shorten one to make room.")}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: 10, border: `1px dashed ${T.n5}`, background: T.white, color: freeSlot ? T.n1 : T.soft, fontSize: 12.5, fontWeight: 600, cursor: freeSlot ? "pointer" : "not-allowed", fontFamily: T.font, opacity: freeSlot ? 1 : 0.65 }}>
        <Icon.plus size={12} /> {freeSlot ? t("workforce.mySchedule.addWindow", "Add a window") : t("workforce.mySchedule.noRoomLeft", "Your windows already cover the whole shift — shorten one to make room.")}
      </button>

      {problem ? (
        <span style={{ fontSize: 12, color: T.red1, fontWeight: 600, lineHeight: 1.45 }}>{problem}</span>
      ) : null}

      <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>
        {t("workforce.mySchedule.availSavedNote", "Saving replaces this weekday's pattern. The colours you pick here are the colours your shift is drawn in, on this schedule and anywhere else the window appears.")}
      </span>
    </>
  );
}

/**
 * Shift editor — the right-hand slide-over (operator, 2026-08-01: "click on
 * those shifts and edit them … should slide out on the right"). One record with
 * several fields → `<Drawer>` per the overlay standard, `panel="shift"` so it
 * rides the URL and Back closes it.
 *
 * Every field is live and validated. The SUBMIT is not: no op can change a
 * published shift from a staff surface (`updateShift` is the admin route, and
 * this persona has `myschedule.read`), and NOTHING in the contract can express a
 * recurrence rule — `WfShift` has no repeat field at all. So the drawer shows
 * exactly what it would send and the send button stays disabled against REQ-123
 * rather than pretending. No optimistic state, no local-only edit.
 */
function ShiftEditDrawer({ date, dates, weekDays, shift, draft, newBreak, template, busy, run, avail, savingAvail, onSaveAvail, onSaved, onClose }: {
  date: string; dates?: string[]; weekDays?: DayState[];
  shift: MyShift | null; draft?: { start: string; end: string }; newBreak?: { start: string; end: string };
  template: ShiftTemplate; busy: string | null;
  run: (id: string, fn: () => Promise<void>) => void;
  /** The advisor's whole pattern — this drawer edits the slice of it that belongs to this weekday. */
  avail: MyAvailability | null; savingAvail: boolean;
  onSaveAvail: (day: string, blocks: AvailabilityBlock[]) => Promise<boolean>;
  onSaved: () => void; onClose: () => void;
}) {
  const t = useT();
  const isNew = !shift;
  const [picked, setPicked] = useState<string[]>(dates && dates.length ? dates : [date]); // ui-local
  const [fixes, setFixes] = useState<Record<string, Fix>>({}); // ui-local — per conflicting day
  const spanDays = picked;
  const multi = spanDays.length > 1;
  const dayOf = (iso: string) => (weekDays || []).find((w) => w.iso === iso);
  const conflicts = spanDays.filter((iso) => !!dayOf(iso)?.shift);
  const fixOf = (iso: string): Fix => fixes[iso] || "skip";
  const writes = spanDays.filter((iso) => !dayOf(iso)?.shift || fixOf(iso) !== "skip").length;
  const [start, setStart] = useState(shift?.start ?? draft?.start ?? "09:00");
  const [end, setEnd] = useState(shift?.end ?? draft?.end ?? "17:00");
  const [breaks, setBreaks] = useState<Brk[]>([
    ...(shift ? shift.breaks.map((b) => ({ ...b })) : []),
    ...(newBreak ? [{ start: newBreak.start, end: newBreak.end, kind: "break" as const }] : []),
  ]); // ui-local
  const [repeat, setRepeat] = useState<"once" | "weekly">("once"); // ui-local
  const [ends, setEnds] = useState<"never" | "on" | "after">("never"); // ui-local
  const [until, setUntil] = useState(addDaysIso(date, 84)); // ui-local
  const [count, setCount] = useState(12); // ui-local
  const [brkRepeat, setBrkRepeat] = useState<"once" | "daily" | "weekly">("once"); // ui-local

  /* ── Availability tab (operator, 2026-08-04) ── the windows for THIS weekday. Seeded from the
   * fetched pattern and re-seeded whenever it reloads, so the tab always shows what the engine
   * actually holds rather than a local copy that drifted. */
  const apiDay = dowOf(date);
  const [tab, setTab] = useState<"shift" | "avail">("shift"); // ui-local
  const [wins, setWins] = useState<AvailabilityBlock[]>([]); // ui-local
  const [availBase, setAvailBase] = useState("[]"); // ui-local — the loaded / last-saved pattern
  useEffect(() => {
    if (!avail) return;
    const mine = avail.blocks.filter((b) => b.day === apiDay).map((b) => ({ ...b }));
    setWins(mine);
    setAvailBase(availKey(mine));
  }, [avail, apiDay]);
  const availDirty = availKey(wins) !== availBase;
  const bounds = shift ? { start: shift.start, end: shift.end } : null;
  const availErr = useMemo(() => {
    for (const w of wins) {
      if (!String(w.name ?? "").trim()) return t("workforce.mySchedule.needsAName", "Give this window a name.");
      if (hmToMin(w.end) <= hmToMin(w.start)) return t("workforce.mySchedule.endAfterStart", "The end time has to be after the start time.");
      if (!w.methods.inPerson && !w.methods.phone && !w.methods.video) return t("workforce.mySchedule.pickAMethod", "Pick at least one method.");
      if (bounds && (hmToMin(w.start) >= hmToMin(bounds.end) || hmToMin(w.end) <= hmToMin(bounds.start))) return t("workforce.mySchedule.whollyOutside", "A window falls entirely outside your shift — nothing in it could ever be booked.");
    }
    const ordered = [...wins].sort((a, b) => hmToMin(a.start) - hmToMin(b.start));
    for (let i = 1; i < ordered.length; i++) {
      if (hmToMin(ordered[i].start) < hmToMin(ordered[i - 1].end)) return t("workforce.mySchedule.windowsOverlap", "Two windows overlap — move one of them.");
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wins, bounds?.start, bounds?.end, t]);
  /** Trim to the published shift on the way out: the overlap is all the engine will serve. */
  const saveWindows = async () => {
    const clamped = wins.map((w) => ({
      ...w,
      start: bounds && w.start < bounds.start ? bounds.start : w.start,
      end: bounds && w.end > bounds.end ? bounds.end : w.end,
      id: w.id.startsWith("local:") ? "" : w.id,
    }));
    if (await onSaveAvail(apiDay, clamped)) setAvailBase(availKey(clamped));
  };

  const span = hmToMin(end) - hmToMin(start);
  const brkMins = breaks.reduce((s, b) => s + Math.max(0, hmToMin(b.end) - hmToMin(b.start)), 0);
  const tooShort = span <= 0;
  const outside = breaks.filter((b) => hmToMin(b.start) < hmToMin(start) || hmToMin(b.end) > hmToMin(end));
  const badBreak = breaks.some((b) => hmToMin(b.end) <= hmToMin(b.start));
  const sorted = [...breaks].sort((a, b) => hmToMin(a.start) - hmToMin(b.start));
  const overlap = sorted.some((b, i) => i > 0 && hmToMin(b.start) < hmToMin(sorted[i - 1].end));
  const invalid = tooShort || badBreak || overlap || outside.length > 0;
  const moved = !!shift && (start !== shift.start || end !== shift.end);
  const brksChanged = !!shift && JSON.stringify(breaks) !== JSON.stringify(shift.breaks);
  const dirty = isNew || moved || brksChanged;
  const seriesPending = repeat === "weekly" || brkRepeat !== "once";
  const cantCreate = isNew && !template;
  const saveId = isNew ? "shift-create" : "shift-save";
  const pendingTitle = t("workforce.mySchedule.needsShiftChangeOp", "Not available yet — needs requestShiftChange (REQ-123)");

  const save = () => run(saveId, async () => {
    try {
      if (isNew) {
        if (!template) return;
        let added = 0, replaced = 0, merged = 0, skipped = 0;
        for (const d of spanDays) {
          const st = dayOf(d);
          if (st?.off === "approved") { skipped++; continue; }
          if (st?.shift) {
            const fix = fixOf(d);
            if (fix === "skip") { skipped++; continue; }
            if (fix === "replace") { await updateShift(st.shift.id, { start, end, breaks }); replaced++; continue; }
            const wideStart = hmToMin(st.shift.start) < hmToMin(start) ? st.shift.start : start;
            const wideEnd = hmToMin(st.shift.end) > hmToMin(end) ? st.shift.end : end;
            await updateShift(st.shift.id, { start: wideStart, end: wideEnd, breaks: st.shift.breaks });
            merged++;
            continue;
          }
          await createShift({ ...template, date: d, start, end, breaks, source: "manual" });
          added++;
        }
        const parts = [
          added ? `${added} ${t("workforce.mySchedule.countAdded", "added")}` : "",
          replaced ? `${replaced} ${t("workforce.mySchedule.countReplaced", "replaced")}` : "",
          merged ? `${merged} ${t("workforce.mySchedule.countMerged", "merged")}` : "",
          skipped ? `${skipped} ${t("workforce.mySchedule.countSkipped", "skipped")}` : "",
        ].filter(Boolean);
        toast({
          tone: added + replaced + merged ? "success" : "info",
          title: multi ? t("workforce.mySchedule.scheduleUpdated", "Schedule updated") : t("workforce.mySchedule.shiftCreated", "Shift added"),
          subtitle: parts.join(" · "),
        });
      } else {
        await updateShift(shift.id, { start, end, breaks });
        toast({ tone: "success", title: t("workforce.mySchedule.shiftSaved", "Schedule updated"), subtitle: t("workforce.mySchedule.managerSees", "Your branch manager sees it on the branch schedule.") });
      }
      onSaved();
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.mySchedule.couldntSaveShift", "Couldn't save that change"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  });
  const setBrk = (i: number, patch: Partial<Brk>) => setBreaks((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const addBrk = (kind: Brk["kind"]) => setBreaks((bs) => {
    const last = [...bs].sort((a, b) => hmToMin(a.end) - hmToMin(b.end))[bs.length - 1];
    const from = Math.min(hmToMin(end) - 30, last ? hmToMin(last.end) + 60 : hmToMin(start) + 180);
    return [...bs, { start: hm(snap15(Math.max(hmToMin(start), from))), end: hm(snap15(Math.max(hmToMin(start), from) + (kind === "lunch" ? 30 : 15))), kind }];
  });

  const card: React.CSSProperties = { background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 };
  const head: React.CSSProperties = { fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700 };
  const inputStyle: React.CSSProperties = { height: 34, padding: "0 10px", border: `1px solid ${T.n5}`, borderRadius: 8, background: T.white, fontFamily: T.fontMono, fontSize: 13, color: T.n1, minWidth: 0 };
  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 };
  const label: React.CSSProperties = { fontSize: 12.5, color: T.n2, fontWeight: 600 };

  /** Radio row. The control (date/number) is a SIBLING of the button, never a
   *  child — interactive content inside a button is invalid and WebKit hands the
   *  click to the button, which made these two modes un-editable there. */
  const Choice = ({ on, onPick, label, children }: { on: boolean; onPick: () => void; label: string; children?: React.ReactNode }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: 8, background: on ? alpha(T.p1, 6) : T.white, border: `1px solid ${on ? T.p1 : T.n6}` }}>
      <button role="radio" aria-checked={on} onClick={onPick}
        style={{ display: "flex", alignItems: "center", gap: 9, flex: children ? "0 0 auto" : "1 1 auto", minWidth: 0, padding: 0, background: "transparent", border: "none", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
        <span style={{ width: 15, height: 15, borderRadius: "50%", flexShrink: 0, border: `1px solid ${on ? T.p1 : T.n5}`, background: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          {on ? <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.p1 }} /> : null}
        </span>
        <span style={{ fontSize: 12.5, color: T.n1, fontWeight: on ? 700 : 500, whiteSpace: "nowrap" }}>{label}</span>
      </button>
      {children}
    </div>
  );

  return (
    <Drawer panel="shift" width={480} testid="mysched-shift-drawer" onClose={onClose}
      title={isNew ? (multi ? t("workforce.mySchedule.addShifts", "Add shifts") : t("workforce.mySchedule.addShift", "Add shift")) : t("workforce.mySchedule.editShift", "Edit shift")}
      sub={shift ? `${dayLabel(date, "full")} · ${hmLabel(shift.start)}–${hmLabel(shift.end)}`
        : multi ? `${dayLabel(spanDays[0], "md")} – ${dayLabel(spanDays[spanDays.length - 1], "md")} · ${spanDays.length} ${t("workforce.mySchedule.days", "days")}`
        : dayLabel(date, "full")}
      bodyStyle={{ background: T.canvas, padding: 16, gap: 14 }}
      footer={tab === "avail" ? (
        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          <Btn variant="secondary" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn style={{ flex: 2, justifyContent: "center" }} data-testid="mysched-avail-save"
            disabled={!availDirty || !!availErr || savingAvail}
            title={availErr ?? undefined}
            onClick={saveWindows}>
            <Icon.check size={13} /> {savingAvail ? "…" : t("workforce.mySchedule.saveAvailability", "Save availability")}
          </Btn>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, width: "100%" }}>
          <Btn variant="secondary" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn style={{ flex: 2, justifyContent: "center" }} data-testid="mysched-shift-save"
            disabled={invalid || !dirty || cantCreate || busy !== null || (isNew && writes === 0)}
            title={cantCreate ? t("workforce.mySchedule.noTemplate", "No shift on this week's roster to take your role and branch from") : undefined}
            onClick={save}>
            <Icon.check size={13} /> {busy === saveId ? "…" : isNew ? (multi ? `${t("workforce.mySchedule.createShift", "Add shift")} × ${writes}` : t("workforce.mySchedule.createShift", "Add shift")) : t("workforce.mySchedule.saveChanges", "Save changes")}
          </Btn>
        </div>
      )}>

      {!isNew ? (
        <div role="tablist" aria-label={t("workforce.mySchedule.shiftSettings", "Shift settings")}
          style={{ position: "sticky", top: -16, zIndex: 5, margin: "-16px -16px 0", padding: "12px 16px 11px", background: T.canvas, borderBottom: `1px solid ${T.border}`, display: "flex", gap: 6 }}>
          {([["shift", t("workforce.mySchedule.tabHours", "Hours & breaks"), null],
             ["avail", t("workforce.mySchedule.tabAvailability", "My availability"), wins.length ? String(wins.length) : null]] as const).map(([id, label, badge]) => {
            const on = tab === id;
            return (
              <button key={id} role="tab" aria-selected={on} data-testid={`mysched-drawer-tab-${id}`} onClick={() => setTab(id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 13px", borderRadius: 8, cursor: "pointer", fontFamily: T.font, fontSize: 12.5,
                  fontWeight: on ? 700 : 600, color: on ? T.p1 : T.n2,
                  background: on ? T.white : "transparent", border: `1px solid ${on ? alpha(T.p1, 40) : "transparent"}`, boxShadow: on ? T.shadowSm : "none" }}>
                {label}
                {badge ? <span style={{ fontSize: 10.5, fontWeight: 700, fontFamily: T.fontMono, color: on ? T.p1 : T.soft, background: on ? T.p4 : T.n6, borderRadius: 999, padding: "1px 6px" }}>{badge}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {tab === "avail" ? (
        avail ? (
          <AvailabilityTab apiDay={apiDay} dayName={weekdayName(date)} shift={bounds} wins={wins} setWins={setWins}
            palette={avail.palette} catalogue={avail.catalogue} problem={availErr} />
        ) : (
          <span style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.mySchedule.loadingAvailability", "Loading your availability…")}</span>
        )
      ) : (<>

      {isNew && weekDays && weekDays.length > 1 ? (
        <div style={card}>
          <span style={head}>{t("workforce.mySchedule.daysSection", "Days")}</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {weekDays.map((w) => {
              const on = spanDays.includes(w.iso);
              const blocked = w.off === "approved";
              const shift = w.shift;
              const clash = shift !== null;
              return (
                <button key={w.iso} disabled={blocked} data-testid="mysched-day-chip" data-date={w.iso} aria-pressed={on}
                  title={blocked ? t("workforce.mySchedule.cantRosterLeave", "Approved time off — can't be rostered over")
                    : shift ? `${t("workforce.mySchedule.alreadyRostered", "Already rostered")} ${hmLabel(shift.start)}–${hmLabel(shift.end)}`
                    : undefined}
                  onClick={() => setPicked((p) => (p.includes(w.iso) ? p.filter((x) => x !== w.iso) : [...p, w.iso].sort()))}
                  style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 1, minWidth: 46, padding: "5px 8px", borderRadius: 8, cursor: blocked ? "not-allowed" : "pointer", fontFamily: T.font,
                    background: blocked ? T.n6 : on ? alpha(T.p1, 10) : T.white,
                    border: `1px solid ${blocked ? T.n5 : on ? T.p1 : T.n5}`, opacity: blocked ? 0.55 : 1 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: on && !blocked ? T.p1 : T.soft }}>{dayLabel(w.iso, "wk")}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, fontFamily: T.fontMono, color: blocked ? T.soft : T.n1 }}>{Number(w.iso.slice(8, 10))}</span>
                  {clash ? <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.orange1 }} /> : null}
                </button>
              );
            })}
          </div>
          {weekDays.some((w) => w.off === "approved") ? (
            <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{t("workforce.mySchedule.leaveBlockedNote", "Days on approved time off are greyed out — they can't be rostered over.")}</span>
          ) : null}
          {spanDays.some((iso) => dayOf(iso)?.off === "pending") ? (
            <span style={{ fontSize: 11.5, color: T.orange1, lineHeight: 1.5 }}>{t("workforce.mySchedule.pendingLeaveNote", "One of these days has time off still awaiting a decision.")}</span>
          ) : null}
        </div>
      ) : null}

      {isNew && conflicts.length ? (
        <div style={{ ...card, borderColor: alpha(T.orange1, 45), background: alpha(T.orange1, 5) }}>
          <span style={head}>
            {conflicts.length === 1
              ? t("workforce.mySchedule.oneConflict", "1 day already has a shift")
              : `${conflicts.length} ${t("workforce.mySchedule.manyConflicts", "days already have a shift")}`}
          </span>
          <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>{t("workforce.mySchedule.conflictAsk", "Choose what happens on each of them.")}</span>
          {conflicts.map((iso) => {
            const st = dayOf(iso);
            const ex = st && st.shift ? st.shift : null;
            if (!ex) return null;
            const cur = fixOf(iso);
            return (
              <div key={iso} data-testid="mysched-conflict-row" data-date={iso} style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 8, borderTop: `1px solid ${alpha(T.orange1, 25)}` }}>
                <span style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <b style={{ fontSize: 12.5, color: T.n1 }}>{dayLabel(iso, "md")}</b>
                  <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{t("workforce.mySchedule.nowLabel", "now")} {nb(hmLabel(ex.start))}–{nb(hmLabel(ex.end))}</span>
                </span>
                <div style={{ display: "flex", padding: 3, background: T.n6, borderRadius: 7, gap: 2 }} role="radiogroup" aria-label={dayLabel(iso, "md")}>
                  {([["skip", t("workforce.mySchedule.fixSkip", "Skip")], ["replace", t("workforce.mySchedule.fixReplace", "Replace")], ["merge", t("workforce.mySchedule.fixMerge", "Merge")]] as [Fix, string][]).map(([id, label]) => (
                    <button key={id} role="radio" aria-checked={cur === id} onClick={() => setFixes((f) => ({ ...f, [iso]: id }))} data-testid={`mysched-fix-${id}`}
                      style={{ flex: 1, border: "none", background: cur === id ? T.white : "transparent", color: cur === id ? T.n1 : T.soft, fontSize: 12, padding: "5px 10px", borderRadius: 5, cursor: "pointer", fontFamily: T.font, fontWeight: cur === id ? 700 : 500, boxShadow: cur === id ? T.shadowSm : "none" }}>
                      {label}
                    </button>
                  ))}
                </div>
                <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>
                  {cur === "skip" ? t("workforce.mySchedule.fixSkipWhat", "Leaves that day exactly as it is.")
                    : cur === "replace" ? `${t("workforce.mySchedule.fixReplaceWhat", "Overwrites it with")} ${nb(hmLabel(start))}–${nb(hmLabel(end))} · ${t("workforce.mySchedule.fixReplaceBreaks", "the breaks below replace its own")}`
                    : `${t("workforce.mySchedule.fixMergeWhat", "Widens it to")} ${nb(hmLabel(hmToMin(ex.start) < hmToMin(start) ? ex.start : start))}–${nb(hmLabel(hmToMin(ex.end) > hmToMin(end) ? ex.end : end))} · ${t("workforce.mySchedule.fixMergeBreaks", "its own breaks are kept")}`}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      <div style={card}>
        <span style={head}>{t("workforce.mySchedule.hoursSection", "Hours")}</span>
        {multi ? (
          <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
            {t("workforce.mySchedule.sameHoursEachDay", "These hours are added to each day you dragged:")}{" "}
            <b style={{ color: T.n1 }}>{spanDays.map((d) => dayLabel(d, "wk")).join(" · ")}</b>
          </span>
        ) : null}
        <div style={rowStyle}>
          <span style={label}>{t("workforce.mySchedule.startTime", "Start time")}</span>
          <input type="time" step={900} value={start} onChange={(e) => setStart(e.target.value)} data-testid="mysched-shift-start" style={inputStyle} />
        </div>
        <div style={rowStyle}>
          <span style={label}>{t("workforce.mySchedule.endTime", "End time")}</span>
          <input type="time" step={900} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="mysched-shift-end" style={inputStyle} />
        </div>
        <div style={{ ...rowStyle, borderTop: `1px solid ${T.n6}`, paddingTop: 11 }}>
          <span style={label}>{t("workforce.mySchedule.netHours", "Net hours")}</span>
          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: T.fontMono, color: tooShort ? T.red1 : T.n1 }}>
            {tooShort ? "—" : durLabel(span - brkMins)}
            {!tooShort && shift && (moved || brksChanged) ? <span style={{ color: T.soft, fontWeight: 400 }}> ({t("workforce.mySchedule.wasValue", "was")} {durLabel(netWorkedMins(shift))})</span> : null}
          </span>
        </div>
        {tooShort ? (
          <span style={{ fontSize: 12, color: T.red1, lineHeight: 1.45 }}>{t("workforce.mySchedule.endAfterStart", "The end time has to be after the start time.")}</span>
        ) : null}
        {overlap ? (
          <span style={{ fontSize: 12, color: T.red1, lineHeight: 1.45 }}>{t("workforce.mySchedule.breaksOverlap", "Two breaks overlap — move one of them.")}</span>
        ) : null}
        {badBreak ? (
          <span style={{ fontSize: 12, color: T.red1, lineHeight: 1.45 }}>{t("workforce.mySchedule.breakEndAfterStart", "A break's end time has to be after its start.")}</span>
        ) : null}
        {outside.length ? (
          <span style={{ fontSize: 12, color: T.orange1, lineHeight: 1.45 }}>
            {t("workforce.mySchedule.breakOutside", "These breaks now fall outside the shift:")}{" "}
            {outside.map((b) => `${t(brkText(b.kind).labelKey, brkText(b.kind).label)} ${hmLabel(b.start)}–${hmLabel(b.end)}`).join(" · ")}
          </span>
        ) : null}
      </div>

      <div style={card}>
        <div style={rowStyle}>
          <span style={head}>{t("workforce.mySchedule.breaksAndBlocks", "Breaks & blocks")}</span>
          <span style={{ display: "flex", gap: 6 }}>
            <button onClick={() => addBrk("break")} data-testid="mysched-add-break" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 6, border: `1px solid ${T.n5}`, background: T.white, color: T.n1, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
              <Icon.plus size={11} /> {t("workforce.mySchedule.break", "Break")}
            </button>
            <button onClick={() => addBrk("lunch")} data-testid="mysched-add-lunch" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 6, border: `1px solid ${T.n5}`, background: T.white, color: T.n1, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
              <Icon.plus size={11} /> {t("workforce.mySchedule.lunch", "Lunch")}
            </button>
          </span>
        </div>
        {breaks.length === 0 ? (
          <span style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.mySchedule.noBreaksYet", "No breaks on this shift yet.")}</span>
        ) : breaks.map((b, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }} data-testid="mysched-break-row" data-id={String(i)}>
            <select value={b.kind} onChange={(e) => setBrk(i, { kind: e.target.value as Brk["kind"] })}
              aria-label={t("workforce.mySchedule.kind", "Kind")}
              style={{ ...inputStyle, height: 30, fontFamily: T.font, fontSize: 12.5, cursor: "pointer", flex: "0 0 auto" }}>
              <option value="break">{t("workforce.mySchedule.break", "Break")}</option>
              <option value="lunch">{t("workforce.mySchedule.lunch", "Lunch")}</option>
            </select>
            <input type="time" step={900} value={b.start} onChange={(e) => setBrk(i, { start: e.target.value })} aria-label={t("workforce.mySchedule.startTime", "Start time")} style={{ ...inputStyle, height: 30, fontSize: 12.5, flex: "1 1 96px" }} />
            <input type="time" step={900} value={b.end} onChange={(e) => setBrk(i, { end: e.target.value })} aria-label={t("workforce.mySchedule.endTime", "End time")} style={{ ...inputStyle, height: 30, fontSize: 12.5, flex: "1 1 96px" }} />
            <button onClick={() => setBreaks((bs) => bs.filter((_, j) => j !== i))} aria-label={t("common.actions.remove", "Remove")}
              style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${T.n6}`, background: T.white, color: T.soft, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
              <Icon.trash size={12} />
            </button>
          </div>
        ))}
        {breaks.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: `1px solid ${T.n6}`, paddingTop: 11 }} role="radiogroup" aria-label={t("workforce.mySchedule.repeatBreaks", "Repeat these breaks")}>
            <span style={head}>{t("workforce.mySchedule.repeatBreaks", "Repeat these breaks")}</span>
            <Choice on={brkRepeat === "once"} onPick={() => setBrkRepeat("once")} label={t("workforce.mySchedule.thisShiftOnly", "This shift only")} />
            <Choice on={brkRepeat === "daily"} onPick={() => setBrkRepeat("daily")} label={t("workforce.mySchedule.everyWorkingDay", "Every working day, at these times")} />
            <Choice on={brkRepeat === "weekly"} onPick={() => setBrkRepeat("weekly")} label={`${t("workforce.mySchedule.everyWeekOn", "Every week on")} ${dayLabel(date, "wk")}`} />
          </div>
        ) : null}
        <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>
          {t("workforce.mySchedule.blocksAreBreaks", "A blocked-out slot is stored as a break — break and lunch are the only kinds the schedule carries today (REQ-123 asks for a labelled block).")}
        </span>
      </div>

      <div style={card} role="radiogroup" aria-label={t("workforce.mySchedule.repeats", "Repeats")}>
        <span style={head}>{t("workforce.mySchedule.repeats", "Repeats")}</span>
        <Choice on={repeat === "once"} onPick={() => setRepeat("once")} label={t("workforce.mySchedule.thisShiftOnly", "This shift only")} />
        <Choice on={repeat === "weekly"} onPick={() => setRepeat("weekly")} label={`${t("workforce.mySchedule.everyWeekOn", "Every week on")} ${dayLabel(date, "wk")}`} />
        {repeat === "weekly" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: `1px solid ${T.n6}`, paddingTop: 11 }} role="radiogroup" aria-label={t("workforce.mySchedule.ends", "Ends")}>
            <span style={head}>{t("workforce.mySchedule.ends", "Ends")}</span>
            <Choice on={ends === "never"} onPick={() => setEnds("never")} label={t("workforce.mySchedule.endsNever", "Never")} />
            <Choice on={ends === "on"} onPick={() => setEnds("on")} label={t("workforce.mySchedule.endsOn", "On")}>
              <input type="date" value={until} min={date} onChange={(e) => { setUntil(e.target.value); setEnds("on"); }} data-testid="mysched-shift-until"
                aria-label={t("workforce.mySchedule.endsOn", "On")}
                style={{ ...inputStyle, height: 30, fontSize: 12.5, opacity: ends === "on" ? 1 : 0.55 }} />
            </Choice>
            <Choice on={ends === "after"} onPick={() => setEnds("after")} label={t("workforce.mySchedule.endsAfter", "After")}>
              <input type="number" min={1} max={52} value={count} onChange={(e) => { setCount(Math.max(1, Math.min(52, Number(e.target.value) || 1))); setEnds("after"); }} data-testid="mysched-shift-count"
                aria-label={t("workforce.mySchedule.occurrences", "occurrences")}
                style={{ ...inputStyle, height: 30, width: 68, fontSize: 12.5, opacity: ends === "after" ? 1 : 0.55 }} />
              <span style={{ fontSize: 12.5, color: T.n2, whiteSpace: "nowrap" }}>{t("workforce.mySchedule.occurrences", "occurrences")}</span>
            </Choice>
          </div>
        ) : null}
      </div>

      {seriesPending ? (
        <div style={{ ...card, borderColor: alpha(T.orange1, 45), background: alpha(T.orange1, 5) }}>
          <span style={head}>{t("workforce.mySchedule.repeatNotSaved", "The repeat rule isn't saved yet")}</span>
          <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
            {t("workforce.mySchedule.repeatNotSavedBody2", "Times and breaks save for this date. The schedule can't hold a repeat rule yet, so the repeat you picked won't be kept.")}
          </span>
        </div>
      ) : null}
      </>)}
    </Drawer>
  );
}

export function DayBoard({ date, day, timeOff, avail, lo, hi, isToday, nowMin, busy, sel, setSel, showAppts, onSwap, onTimeOff, onReportSick, onEditShift, onCreateShift, onBlockRange }: {
  date: string; day: MyDayResponse | null; timeOff: TimeOffEntry[];
  avail?: MyAvailability | null;
  lo: number; hi: number; isToday: boolean; nowMin: number; busy: string | null;
  sel: DaySel | null; setSel: (s: DaySel | null) => void; showAppts: boolean;
  onSwap: () => void; onTimeOff: () => void; onReportSick: () => void; onEditShift: () => void;
  onCreateShift: (start: string, end: string) => void; onBlockRange: (start: string, end: string) => void;
}) {
  const t = useT();
  const minH = DAY_HOUR_H / 60;
  const total = (hi - lo) * minH;
  const hours = Math.round((hi - lo) / 60);
  const y = (m: number) => (Math.max(lo, Math.min(hi, m)) - lo) * minH;
  const axisRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(0); // ui-local — measured width, drives the stacking fallback
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null); // ui-local
  const off = timeOff.find((e) => inRange(date, e.startDate, e.endDate));
  const shift = day?.shift || null;
  /* Availability is not a layer over the shift — it IS the shift's colour (operator, 2026-08-04). */
  const segs = segsFor(date, avail ?? null, shift ? { start: shift.start, end: shift.end } : null);
  const palette = avail?.palette ?? [];
  const CARDS_W = 320, GAP = 16, AXIS_W = 56, MIN_ROSTER = 300;
  const sideBySide = w === 0 || w >= CARDS_W + GAP + AXIS_W + MIN_ROSTER;

  const minAt = (clientY: number) => {
    const el = axisRef.current;
    if (!el) return lo;
    const r = el.getBoundingClientRect();
    return Math.max(lo, Math.min(hi, snap15(lo + (clientY - r.top) / minH)));
  };
  const dragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || e.target !== e.currentTarget) return;
    const a = minAt(e.clientY);
    setDrag({ a, b: a });
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const dragMove = (e: React.PointerEvent<HTMLDivElement>) => { if (drag) setDrag({ a: drag.a, b: minAt(e.clientY) }); };
  const dragEnd = () => {
    if (!drag) return;
    const start = Math.min(drag.a, drag.b), end = Math.max(drag.a, drag.b);
    setDrag(null);
    if (end - start < 15) { setSel(null); return; }
    // No shift on this day → the drag IS the new shift (operator, 2026-08-01).
    if (!day?.shift) { onCreateShift(hm(start), hm(end)); return; }
    setSel({ kind: "range", start, end });
  };
  const dragProps = { onPointerDown: dragStart, onPointerMove: dragMove, onPointerUp: dragEnd, onPointerCancel: dragEnd };
  const band = drag ? { start: Math.min(drag.a, drag.b), end: Math.max(drag.a, drag.b) }
    : sel && sel.kind === "range" ? { start: sel.start, end: sel.end } : null;
  const bandLabel = band ? `${hmLabel(hm(band.start))} – ${hmLabel(hm(band.end))} · ${durLabel(band.end - band.start)}` : undefined;

  const headCell: React.CSSProperties = { padding: "9px 12px", borderBottom: `1px solid ${T.border}`, background: T.white, display: "flex", flexDirection: "column", gap: 4, minWidth: 0, overflow: "hidden", position: "sticky", top: 0, zIndex: 6 };
  const boardRef = useRef<HTMLDivElement | null>(null);
  // Fixed 06:00–23:00 window (operator) — open on the rostered hours.
  useEffect(() => {
    const el = boardRef.current;
    if (el) el.scrollTop = Math.max(0, ((shift ? hmToMin(shift.start) : 8 * 60) - lo) * minH - 24);
  }, [shift, lo, minH]);

  return (
    <div ref={wrapRef} style={{ display: "grid", gridTemplateColumns: sideBySide ? `minmax(0,1fr) ${CARDS_W}px` : "minmax(0,1fr)", gap: GAP, alignItems: "flex-start" }}>
      <div ref={boardRef} style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "auto", maxHeight: GRID_MAX_H }}>
        <div style={{ display: "grid", gridTemplateColumns: `${AXIS_W}px minmax(${MIN_ROSTER}px,1fr)`, minWidth: "min-content" }}>
          <div style={{ ...headCell, borderRight: `1px solid ${T.n6}` }} />
          <div style={{ ...headCell, background: isToday ? T.p4 : T.white }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 7, minWidth: 0, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: isToday ? T.p1 : T.soft }}>{dayLabel(date, "wk")}</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: isToday ? T.p1 : T.n1, fontFamily: T.fontMono }}>{Number(date.slice(8, 10))}</span>
              <span style={{ fontSize: 12.5, color: T.soft }}>{dayLabel(date, "full")}</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 5, flexWrap: "wrap" }}>
                {off ? <Chip tone={off.status === "approved" ? T.green1 : T.orange1}>{off.type}{off.status === "pending" ? ` · ${t("workforce.mySchedule.pending", "pending")}` : ""}</Chip> : null}
                {shift ? <ShiftChip start={shift.start} end={shift.end} /> : null}
              </span>
            </div>
          </div>

          {/* time axis — drag it to select a range */}
          <div ref={axisRef} {...dragProps} title={t("workforce.mySchedule.dragAxis", "Drag to select a time range")}
            style={{ position: "relative", height: total, borderRight: `1px solid ${T.n6}`, background: T.white, cursor: "ns-resize", touchAction: "none" }}>
            {Array.from({ length: hours + 1 }, (_, h) => (
              <span key={h} style={{ position: "absolute", top: h * DAY_HOUR_H - 6, right: 8, fontSize: 10, color: T.soft, fontFamily: T.fontMono, pointerEvents: "none" }}>
                {h === hours ? "" : hmLabel(hm(lo + h * 60))}
              </span>
            ))}
            {band ? <div aria-hidden style={{ position: "absolute", left: 0, right: 0, top: (band.start - lo) * minH, height: Math.max(2, (band.end - band.start) * minH), background: alpha(T.p1, 16), borderTop: `1px solid ${T.p1}`, borderBottom: `1px solid ${T.p1}`, pointerEvents: "none" }} /> : null}
          </div>

          {/* the roster */}
          <div {...dragProps} style={{ position: "relative", height: total, background: alpha(T.n5, 14), touchAction: "none", cursor: "ns-resize" }}>
            <div aria-hidden style={{ position: "absolute", inset: 0, background: `repeating-linear-gradient(to bottom, ${T.n6} 0 1px, transparent 1px ${DAY_HOUR_H}px)`, pointerEvents: "none" }} />
            {off ? (
              <div style={{ position: "absolute", inset: 0, background: off.status === "approved" ? alpha(T.green1, 9) : alpha(T.orange1, 8), border: `1px ${off.status === "approved" ? "solid" : "dashed"} ${alpha(off.status === "approved" ? T.green1 : T.orange1, 40)}`, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 14 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: off.status === "approved" ? T.green1 : T.orange1, background: T.white, padding: "3px 9px", borderRadius: 999, border: `1px solid ${alpha(off.status === "approved" ? T.green1 : T.orange1, 35)}` }}>
                  {off.type}{off.status === "pending" ? ` · ${t("workforce.mySchedule.pending", "pending")}` : ""}
                </span>
              </div>
            ) : null}
            <div style={{ position: "absolute", left: 0, width: showAppts ? 150 : undefined, right: showAppts ? undefined : 0, top: 0, bottom: 0, borderRight: showAppts ? `1px solid ${T.n6}` : undefined }}>
              <RosterColumn day={day} off={off} lo={lo} minH={minH} sel={sel} onSelect={setSel} onEdit={onEditShift} segs={segs} palette={palette} />
            </div>
            {showAppts ? (
              <div style={{ position: "absolute", left: 158, right: 0, top: 0, bottom: 0 }}>
                {packLanes<ApptSpan>(apptSpans(day)).map((p) => (
                  <ApptBlock key={p.item.appt.id} a={p.item.appt} lane={p.lane} lanes={p.lanes} top={y(p.item.start)} height={y(p.item.end) - y(p.item.start)} wide />
                ))}
              </div>
            ) : null}
            {band ? <RangeBand start={band.start} end={band.end} lo={lo} minH={minH} label={bandLabel} /> : null}
            {isToday && nowMin >= lo && nowMin <= hi ? (
              <div aria-hidden style={{ position: "absolute", left: 0, right: 0, top: y(nowMin), height: 2, background: T.red1, zIndex: 5 }}>
                <span style={{ position: "absolute", left: -3, top: -3, width: 8, height: 8, borderRadius: "50%", background: T.red1 }} />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, position: sideBySide ? "sticky" : "static", top: 14 }}>
        <SelectionPanel day={day} sel={sel} isToday={isToday} busy={busy}
          onSwap={onSwap} onTimeOff={onTimeOff} onReportSick={onReportSick} onEdit={onEditShift}
          onBlockRange={() => { if (sel && sel.kind === "range") onBlockRange(hm(sel.start), hm(sel.end)); }}
          onClear={() => setSel(null)} />
      </div>
    </div>
  );
}

/* ── Week — one column per date, roster only ──────────────────────────── */

function TimeGrid({ dates, days, timeOff, lo, hi, todayIso, nowMin, showAppts, avail, onEditShift, onCreateShift }: {
  dates: string[]; days: (MyDayResponse | null)[]; timeOff: TimeOffEntry[];
  lo: number; hi: number; todayIso: string; nowMin: number; showAppts: boolean;
  avail: MyAvailability | null;
  onEditShift: (date: string, shift: MyShift) => void;
  onCreateShift: (dates: string[], start: string, end: string) => void;
}) {
  const t = useT();
  const minH = HOUR_H / 60;
  const total = (hi - lo) * minH;
  const hours = Math.round((hi - lo) / 60);
  const y = (min: number) => (Math.max(lo, Math.min(hi, min)) - lo) * minH;
  const cols = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const firstStart = days.find((d) => d?.shift)?.shift?.start;
  // Land on the first rostered hour rather than on 06:00.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = Math.max(0, ((firstStart ? hmToMin(firstStart) : 8 * 60) - lo) * minH - 24);
  }, [firstStart, lo, minH]);
  const [drag, setDrag] = useState<{ d0: string; d1: string; a: number; b: number } | null>(null); // ui-local
  /** Drag an EMPTY day to create a shift — 15-minute snap, and drag SIDEWAYS to
   *  add the same hours to the other days in the week (operator, 2026-08-01). */
  const minAt = (d: string, clientY: number) => {
    const el = cols.current[d];
    if (!el) return lo;
    const r = el.getBoundingClientRect();
    return Math.max(lo, Math.min(hi, snap15(lo + (clientY - r.top) / minH)));
  };
  const dateAt = (clientX: number) => {
    for (const d of dates) {
      const el = cols.current[d];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) return d;
    }
    return null;
  };
  const spanOf = (d0: string, d1: string) => {
    const i0 = dates.indexOf(d0), i1 = dates.indexOf(d1);
    return dates.slice(Math.min(i0, i1), Math.max(i0, i1) + 1);
  };
  const inDrag = (d: string) => !!drag && spanOf(drag.d0, drag.d1).includes(d);
  /** Approved leave can't be rostered over, so a drag never picks it up. */
  const isBlocked = (d: string) => timeOff.some((e) => inRange(d, e.startDate, e.endDate) && e.status === "approved");
  const dragProps = (d: string, empty: boolean) => (empty ? {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const a = minAt(d, e.clientY);
      setDrag({ d0: d, d1: d, a, b: a });
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      if (!drag) return;
      setDrag({ ...drag, d1: dateAt(e.clientX) || drag.d1, b: minAt(drag.d0, e.clientY) });
    },
    onPointerUp: () => {
      if (!drag) return;
      const s = Math.min(drag.a, drag.b), e2 = Math.max(drag.a, drag.b);
      // Days that already have a shift COME ALONG — the drawer asks what to do
      // with each of them (operator, 2026-08-01). Approved leave never does.
      const span = spanOf(drag.d0, drag.d1).filter((x) => !isBlocked(x));
      setDrag(null);
      if (e2 - s >= 15 && span.length) onCreateShift(span, hm(s), hm(e2));
    },
    onPointerCancel: () => setDrag(null),
    style: { cursor: "ns-resize" as const, touchAction: "none" as const },
  } : {});

  return (
    <div ref={scrollRef} style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "auto", maxHeight: GRID_MAX_H }}>
      <div style={{ display: "grid", gridTemplateColumns: `56px repeat(${dates.length}, minmax(0,1fr))` }}>
        <div style={{ borderBottom: `1px solid ${T.border}`, borderRight: `1px solid ${T.n6}`, background: T.white, position: "sticky", top: 0, zIndex: 7 }} />
        {dates.map((d, i) => {
          const day = days[i];
          const isToday = d === todayIso;
          const off = timeOff.find((e) => inRange(d, e.startDate, e.endDate));
          return (
            <div key={d} style={{ padding: "9px 10px", borderBottom: `1px solid ${T.border}`, borderRight: i === dates.length - 1 ? "none" : `1px solid ${T.n6}`, background: isToday ? T.p4 : T.white, display: "flex", flexDirection: "column", gap: 5, minWidth: 0, overflow: "hidden", position: "sticky", top: 0, zIndex: 6 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: isToday ? T.p1 : T.soft }}>{dayLabel(d, "wk")}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: isToday ? T.p1 : T.n1, fontFamily: T.fontMono }}>{Number(d.slice(8, 10))}</span>
              </div>
              {/* plain text, not a pill: seven columns at ~80px can't hold a
                  bordered nowrap chip, and hmLabel renders 12-hour times. */}
              {day?.shift
                ? <RosterTime start={day.shift.start} end={day.shift.end} />
                : <span style={{ fontSize: 10.5, color: T.soft, fontFamily: T.fontMono, lineHeight: 1.25 }}>{t("workforce.mySchedule.notScheduled", "Not scheduled")}</span>}
              {off ? <Chip tone={off.status === "approved" ? T.green1 : T.orange1}>{off.type}{off.status === "pending" ? ` · ${t("workforce.mySchedule.pending", "pending")}` : ""}</Chip> : null}
            </div>
          );
        })}

        {/* hour gutter */}
        <div style={{ position: "relative", height: total, borderRight: `1px solid ${T.n6}`, background: T.white }}>
          {Array.from({ length: hours + 1 }, (_, h) => (
            <span key={h} style={{ position: "absolute", top: h * HOUR_H - 6, right: 8, fontSize: 10, color: T.soft, fontFamily: T.fontMono }}>
              {h === hours ? "" : hmLabel(hm(lo + h * 60))}
            </span>
          ))}
        </div>

        {dates.map((d, i) => {
          const day = days[i];
          const isToday = d === todayIso;
          const shift = day?.shift || null;
          const off = timeOff.find((e) => inRange(d, e.startDate, e.endDate));
          const sTop = shift ? y(hmToMin(shift.start)) : 0;
          const sH = shift ? y(hmToMin(shift.end)) - sTop : 0;
          return (
            <div key={d} ref={(el) => { cols.current[d] = el; }} {...dragProps(d, !shift && !isBlocked(d))}
              title={!shift && !isBlocked(d) ? t("workforce.mySchedule.dragToCreateSpan", "Drag to add a shift — 15-minute snap; drag sideways to add it to more days") : undefined}
              style={{ position: "relative", height: total, borderRight: i === dates.length - 1 ? "none" : `1px solid ${T.n6}`, background: alpha(T.n5, 14), ...(dragProps(d, !shift && !isBlocked(d)).style || {}) }}>
              {shift ? (() => {
                /* Same segments and same renderer as the day view — one implementation, so a
                   window cannot look different depending on which view you opened. */
                const segs = segsFor(d, avail, { start: shift.start, end: shift.end });
                const skin = shiftSkin(segs, avail?.palette ?? [], false);
                return (
                <button onClick={() => onEditShift(d, shift)} data-testid="mysched-shift-block" data-id={shift.id}
                  title={`${t("workforce.mySchedule.editShift", "Edit shift")} · ${hmLabel(shift.start)}–${hmLabel(shift.end)} · ${durLabel(netWorkedMins(shift))} ${t("workforce.mySchedule.net", "net")}`}
                  style={{ position: "absolute", left: 4, right: showAppts ? undefined : 4, width: showAppts ? APPT_SPINE : undefined, top: sTop, height: Math.max(20, sH), background: skin.background, border: skin.border, borderLeft: skin.borderLeft, borderRadius: 7, padding: showAppts ? 0 : "5px 8px", cursor: "pointer", textAlign: "left", fontFamily: T.font, display: "flex", flexDirection: "column", gap: 3, overflow: "hidden" }}>
                  <ShiftFill segs={segs} palette={avail?.palette ?? []} top0={sTop} y={y} labels={showAppts ? "none" : "left"} />
                  {showAppts ? null : (
                    <>
                      {/* Only label the block "On shift" when no window names it — otherwise the
                          availability name IS the label, which is the point of the change. */}
                      {segs.length ? null : <span style={{ fontSize: 9.5, fontWeight: 700, color: T.p1, textTransform: "uppercase", letterSpacing: 0.06, whiteSpace: "nowrap" }}>{t("workforce.mySchedule.onShift", "On shift")}</span>}
                      {sH >= 46 && !segs.length ? <RosterTime start={shift.start} end={shift.end} size={11} /> : null}
                      {sH >= 120 && !segs.length ? <span style={{ fontSize: 10.5, color: T.soft }}>{durLabel(netWorkedMins(shift))} {t("workforce.mySchedule.net", "net")}</span> : null}
                    </>
                  )}
                </button>
                );
              })() : null}
              <div aria-hidden style={{ position: "absolute", inset: 0, background: `repeating-linear-gradient(to bottom, ${T.n6} 0 1px, transparent 1px ${HOUR_H}px)`, pointerEvents: "none" }} />

              {(shift?.breaks || []).map((b, bi) => {
                const top = y(hmToMin(b.start)), h = y(hmToMin(b.end)) - top;
                const txt = brkText(b.kind);
                return (
                  <div key={bi} title={`${t(txt.labelKey, txt.label)} ${hmLabel(b.start)}–${hmLabel(b.end)}`}
                    style={{ position: "absolute", left: showAppts ? 6 : 10, right: showAppts ? undefined : 10, width: showAppts ? APPT_SPINE - 4 : undefined, top, height: Math.max(12, h), background: HATCH, border: `1px dashed ${T.n5}`, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {!showAppts && h >= 18 ? <span style={{ fontSize: 9.5, fontWeight: 700, color: T.n2, textTransform: "uppercase", letterSpacing: 0.05, whiteSpace: "nowrap" }}>{t(txt.labelKey, txt.label)}</span> : null}
                  </div>
                );
              })}

              {off ? (
                <div style={{ position: "absolute", inset: 0, background: off.status === "approved" ? alpha(T.green1, 9) : alpha(T.orange1, 8), border: `1px ${off.status === "approved" ? "solid" : "dashed"} ${alpha(off.status === "approved" ? T.green1 : T.orange1, 40)}`, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 14 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: off.status === "approved" ? T.green1 : T.orange1, background: T.white, padding: "3px 9px", borderRadius: 999, border: `1px solid ${alpha(off.status === "approved" ? T.green1 : T.orange1, 35)}` }}>
                    {off.type}{off.status === "pending" ? ` · ${t("workforce.mySchedule.pending", "pending")}` : ""}
                  </span>
                </div>
              ) : null}

              {showAppts ? (
                <div style={{ position: "absolute", left: APPT_SPINE + 6, right: 2, top: 0, bottom: 0 }}>
                  {packLanes<ApptSpan>(apptSpans(day)).map((p) => (
                    <ApptBlock key={p.item.appt.id} a={p.item.appt} lane={p.lane} lanes={p.lanes} top={y(p.item.start)} height={y(p.item.end) - y(p.item.start)} wide={false} />
                  ))}
                </div>
              ) : null}

              {drag && inDrag(d) ? <RangeBand start={Math.min(drag.a, drag.b)} end={Math.max(drag.a, drag.b)} lo={lo} minH={minH} label={`${hmLabel(hm(Math.min(drag.a, drag.b)))}–${hmLabel(hm(Math.max(drag.a, drag.b)))}`} /> : null}

              {isToday && nowMin >= lo && nowMin <= hi ? (
                <div aria-hidden style={{ position: "absolute", left: 0, right: 0, top: y(nowMin), height: 2, background: T.red1, zIndex: 5 }}>
                  <span style={{ position: "absolute", left: -3, top: -3, width: 8, height: 8, borderRadius: "50%", background: T.red1 }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Month — rostered hours, day by day ───────────────────────────────── */

function MonthGrid({ anchor, roster, timeOff, todayIso, onOpenShift }: {
  anchor: string; roster: ViewData["roster"]; timeOff: TimeOffEntry[]; todayIso: string;
  onOpenShift: (iso: string, shift: MyShift | null) => void;
}) {
  const t = useT();
  const { y, m, dim } = monthBounds(anchor);
  const iso = (d: number) => `${anchor.slice(0, 7)}-${pad2(d)}`;
  const startDow = (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7;
  const cells: (number | null)[] = [...Array.from({ length: startDow }, () => null), ...Array.from({ length: dim }, (_, i) => i + 1)];
  const dows = [
    t("workforce.mySchedule.mon", "Mon"), t("workforce.mySchedule.tue", "Tue"), t("workforce.mySchedule.wed", "Wed"),
    t("workforce.mySchedule.thu", "Thu"), t("workforce.mySchedule.fri", "Fri"), t("workforce.mySchedule.sat", "Sat"), t("workforce.mySchedule.sun", "Sun"),
  ];
  /** Month totals. Rostered net hours — a day off contributes nothing, and only
   *  days already gone count towards "worked to date". */
  const totals = useMemo(() => {
    let past = 0, all = 0, offDays = 0, sickDays = 0;
    for (let d = 1; d <= dim; d++) {
      const cIso = `${anchor.slice(0, 7)}-${pad2(d)}`;
      const off = timeOff.find((e) => inRange(cIso, e.startDate, e.endDate));
      if (off) { if (isSick(off)) sickDays++; else offDays++; continue; }
      const s = roster[cIso];
      if (!s) continue;
      const m = netOf(s);
      all += m;
      if (cIso < todayIso) past += m;
    }
    return { past, all, offDays, sickDays };
  }, [anchor, dim, roster, timeOff, todayIso]);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, padding: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", gap: 6 }}>
        {dows.map((d) => <div key={d} style={{ textAlign: "center", paddingBottom: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.06, textTransform: "uppercase", color: T.soft }}>{d}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const cIso = iso(d);
          const off = timeOff.find((e) => inRange(cIso, e.startDate, e.endDate));
          // Operator rule: time off (or sick) means NO scheduled hours on that day.
          const shift = off ? undefined : roster[cIso];
          const mins = shift ? netOf(shift) : 0;
          const sick = off ? isSick(off) : false;
          const tone = off ? (sick ? T.red1 : T.green1) : T.p1;
          const isToday = cIso === todayIso;
          const past = cIso < todayIso;
          return (
            <button key={cIso} onClick={() => { if (!off) onOpenShift(cIso, shift ? { id: shift.id, start: shift.start, end: shift.end, breaks: shift.breaks } : null); }}
              data-testid="mysched-month-cell" data-date={cIso} data-mins={String(mins)}
              title={off ? undefined : shift ? `${t("workforce.mySchedule.editShift", "Edit shift")} · ${hmLabel(shift.start)}–${hmLabel(shift.end)}` : t("workforce.mySchedule.addShift", "Add shift")}
              style={{ minHeight: 96, borderRadius: 9, cursor: off ? "default" : "pointer", fontFamily: T.font, textAlign: "left", padding: 8, display: "flex", flexDirection: "column", gap: 5, alignItems: "stretch", background: off ? alpha(tone, 8) : shift ? alpha(T.p1, 5) : T.white, border: `1px solid ${isToday ? T.p1 : T.n6}`, boxShadow: isToday ? `0 0 0 2px ${T.p3}` : "none", overflow: "hidden", opacity: past && !off && !shift ? 0.7 : 1 }}>
              <span style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: isToday ? T.p1 : T.n1, fontFamily: T.fontMono }}>{d}</span>
                {mins ? <span style={{ fontSize: 13, fontWeight: 700, color: T.p1, fontFamily: T.fontMono }}>{durLabel(mins)}</span> : null}
              </span>
              {off ? (
                <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Chip tone={tone}>{sick ? t("workforce.mySchedule.sick", "Sick") : off.type}</Chip>
                  <span style={{ fontSize: 10.5, color: T.soft }}>
                    {off.status === "pending" ? t("workforce.mySchedule.pending", "pending") : t("workforce.mySchedule.noHours", "no hours")}
                  </span>
                </span>
              ) : shift ? (
                <RosterTime start={shift.start} end={shift.end} />
              ) : (
                <span style={{ fontSize: 10.5, color: T.soft, lineHeight: 1.25 }}>{t("workforce.mySchedule.notScheduled", "Not scheduled")}</span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 26, flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.n6}` }}>
        <Stat label={t("workforce.mySchedule.workedToDate", "Worked to date")} value={durLabel(totals.past)} sub={t("workforce.mySchedule.daysPassed", "days that have passed")} />
        <Stat label={t("workforce.mySchedule.rosteredMonth", "Rostered this month")} value={durLabel(totals.all)} />
        <Stat label={t("workforce.mySchedule.timeOffDays", "Time off")} value={`${totals.offDays}d`} />
        <Stat label={t("workforce.mySchedule.sickDays", "Sick")} value={`${totals.sickDays}d`} />
        <span style={{ flex: "1 1 260px", minWidth: 220, fontSize: 11, color: T.soft, lineHeight: 1.5 }}>
          {t("workforce.mySchedule.monthTotalsNote", "Totals come off the roster: a day off carries no hours, an unscheduled day carries none either. Part-days (going home sick mid-shift) can't be counted yet — time off has no start/end times and there is no worked-hours op. REQ-123.")}
        </span>
      </div>
    </div>
  );
}

/* ── The surface ─────────────────────────────────────────────────────── */

export function ScheduleView({ mode, setMode, anchor, setAnchor, dates, days, roster, sched, myTime, loading, error, busy, run, todayIso, onReload, onRequestTimeOff, onReportSick }: {
  mode: Mode; setMode: (m: Mode) => void; anchor: string; setAnchor: (iso: string) => void;
  dates: string[]; days: (MyDayResponse | null)[]; roster: ViewData["roster"];
  sched: MyScheduleResponse | null; myTime: MyTimeResponse | null;
  loading: boolean; error: string | null; busy: string | null;
  run: (id: string, fn: () => Promise<void>) => void;
  todayIso: string; onReload: () => void;
  onRequestTimeOff: (iso: string) => void;
  onReportSick: (iso: string) => void;
}) {
  const t = useT();
  const [sel, setSel] = useState<DaySel | null>(null); // ui-local
  const [edit, setEdit] = useState<{ date: string; dates?: string[]; shift: MyShift | null; draft?: { start: string; end: string }; newBreak?: { start: string; end: string } } | null>(null); // ui-local
  // Operator, 2026-08-01: appointments are OFF by default on the schedule.
  const [showAppts, setShowAppts] = useState(false); // ui-local
  // A create needs staffId/role/locationId, which MyDayResponse.shift doesn't
  // carry — take them off the banker's own roster rather than inventing them.
  const template = useMemo<ShiftTemplate>(() => {
    const s = (sched?.shifts || []).find((x) => x.staffId);
    return s ? { staffId: s.staffId, role: s.role, locationId: s.locationId } : null;
  }, [sched]);
  // A selection belongs to one day and one shift — don't carry it across.
  useEffect(() => { setSel(null); setEdit(null); }, [anchor, mode]);
  const timeOff = myTime?.timeOffEntries || [];
  /** Every day of the visible week, with what is already on it — the add-shift
   *  drawer's day chips and conflict rows read this. */
  const weekDayStates = useMemo<DayState[]>(() => dates.map((iso, i) => {
    const off = timeOff.find((e) => inRange(iso, e.startDate, e.endDate));
    return { iso, shift: days[i]?.shift || null, off: off ? (off.status === "approved" ? "approved" : "pending") : null };
  }), [dates, days, timeOff]);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  /** The window the grid draws: the roster's own extremes, padded to the hour. */
  const { lo, hi } = useMemo(() => {
    let a = DAY_START_MIN, b = DAY_END_MIN;
    // The window is fixed, but never hide a real record that falls outside it.
    for (const d of days) {
      if (!d) continue;
      if (d.shift) {
        a = Math.min(a, hmToMin(d.shift.start));
        b = Math.max(b, hmToMin(d.shift.end));
      }
      if (showAppts) for (const ap of d.appointments) { a = Math.min(a, hmToMin(ap.start)); b = Math.max(b, hmToMin(ap.end)); }
    }
    return { lo: Math.max(0, Math.floor(a / 60) * 60), hi: Math.min(24 * 60, Math.ceil(b / 60) * 60) };
  }, [days, showAppts]);

  const step = (dir: number) => {
    if (mode === "day") { setAnchor(addDaysIso(anchor, dir)); return; }
    if (mode === "week") { setAnchor(addDaysIso(mondayOf(anchor), dir * 7)); return; }
    const y = Number(anchor.slice(0, 4)), m = Number(anchor.slice(5, 7)) - 1;
    setAnchor(new Date(Date.UTC(y, m + dir, 1)).toISOString().slice(0, 10));
  };
  const title = useMemo(() => {
    if (mode === "day") return dayLabel(anchor, "full");
    if (mode === "month") return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${anchor.slice(0, 7)}-01T00:00:00Z`));
    const ws = mondayOf(anchor);
    return `${dayLabel(ws, "md")} – ${dayLabel(addDaysIso(ws, 6), "md")}`;
  }, [mode, anchor]);

  const doSwap = (shiftId: string) => run("swap", async () => {
    try { await createSwapRequest({ shiftId, route: "marketplace" }); toast({ tone: "success", title: t("workforce.mySchedule.swapPosted", "Shift offered for swap"), subtitle: t("workforce.mySchedule.swapPostedSub", "Track it under Swaps.") }); onReload(); }
    catch (e) { toast({ tone: "danger", title: t("workforce.mySchedule.couldntSwap", "Couldn't post that swap"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  const dayData = mode === "day" ? days[0] || null : null;
  /* The advisor's own pattern, fetched once for every mode — one source, so the day and the week
     can never disagree about what a window is for. It is never drawn as a layer: it colours the
     shift blocks themselves, and every setting for it lives on the shift drawer's second tab. */
  const availRes = useWfResource<MyAvailability>("sched:avail", getMyAvailability);
  const avail = availRes.data ?? null;
  const [savingAvail, setSavingAvail] = useState(false); // ui-local
  /** Whole-list PUT (the engine validates the pattern as a set): this weekday's windows replace the
   *  old ones, every other day passes straight through. */
  const saveAvail = async (day: string, dayBlocks: AvailabilityBlock[]) => {
    setSavingAvail(true);
    try {
      const rest = (avail?.blocks ?? []).filter((b) => b.day !== day);
      await saveMyAvailability([...rest, ...dayBlocks].map((b) => ({
        id: b.id, day: b.day, start: b.start, end: b.end, methods: b.methods,
        services: b.services, note: b.note ?? null, name: b.name ?? null, color: b.color,
      })));
      availRes.reload();
      toast({ tone: "success", title: t("workforce.mySchedule.availabilitySaved", "Availability updated"), subtitle: t("workforce.mySchedule.availabilitySavedSub", "Your shifts are drawn in these colours from now on.") });
      return true;
    } catch (e) {
      toast({ tone: "danger", title: t("workforce.mySchedule.couldntSaveAvailability", "Couldn't save that window"), subtitle: e instanceof Error ? e.message : String(e) });
      return false;
    } finally {
      setSavingAvail(false);
    }
  };
  /** The colour key under the grid — what is actually painted in the range on screen, not the
   *  whole pattern, so it explains the shifts you can see. */
  const shownWindows = useMemo(() => {
    const seen = new Map<string, { name: string; hex: string }>();
    const pairs = mode === "day"
      ? [{ d: anchor, s: dayData?.shift ?? null }]
      : dates.map((d, i) => ({ d, s: days[i]?.shift ?? null }));
    for (const { d, s } of pairs) {
      for (const seg of segsFor(d, avail, s ? { start: s.start, end: s.end } : null)) {
        const key = seg.block.name || seg.block.id;
        if (!seen.has(key)) seen.set(key, { name: key, hex: paletteHex(avail?.palette ?? [], seg.block.color) });
      }
    }
    return [...seen.values()];
  }, [mode, anchor, dates, days, dayData, avail]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "10px 14px", border: `1px solid ${T.border}`, borderRadius: 12, background: T.white }}>
        <Segmented mode={mode} onChange={setMode} />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button aria-label={t("workforce.mySchedule.previous", "Previous")} onClick={() => step(-1)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, cursor: "pointer", color: T.n2 }}><Icon.chev dir="left" size={13} /></button>
          <span style={{ fontSize: 14, fontWeight: 700, minWidth: 190, textAlign: "center", color: T.n1 }}>{title}</span>
          <button aria-label={t("workforce.mySchedule.next", "Next")} onClick={() => step(1)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, cursor: "pointer", color: T.n2 }}><Icon.chev dir="right" size={13} /></button>
          {anchor !== todayIso ? <Btn size="sm" variant="secondary" onClick={() => setAnchor(todayIso)}>{t("workforce.mySchedule.today", "Today")}</Btn> : null}
        </div>
        {mode === "month" ? null : (
          <button onClick={() => setShowAppts((v) => !v)} data-testid="mysched-appts-toggle" role="switch" aria-checked={showAppts}
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 10px 5px 6px", borderRadius: 999, border: `1px solid ${showAppts ? T.p1 : T.n5}`, background: showAppts ? alpha(T.p1, 8) : T.white, cursor: "pointer", fontFamily: T.font }}>
            <span style={{ width: 28, height: 16, borderRadius: 999, background: showAppts ? T.p1 : T.n5, position: "relative", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 2, left: showAppts ? 14 : 2, width: 12, height: 12, borderRadius: "50%", background: T.white }} />
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: showAppts ? T.p1 : T.n2, whiteSpace: "nowrap" }}>{t("workforce.mySchedule.showAppointments", "Show appointments")}</span>
          </button>
        )}
        <span style={{ marginLeft: mode === "month" ? "auto" : 0, fontSize: 11.5, color: T.soft }}>{t("workforce.mySchedule.rosterHint", "Your rostered hours and breaks · drag an empty day to add a shift")}</span>
      </div>

      {error ? (
        <div style={{ border: `1px solid ${alpha(T.red1, 35)}`, background: alpha(T.red1, 6), borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: T.n1 }}>{t("workforce.mySchedule.loadFailed", "Couldn't load the schedule.")} {error}</span>
          <Btn size="sm" variant="secondary" style={{ marginLeft: "auto" }} onClick={onReload}>{t("workforce.mySchedule.retry", "Retry")}</Btn>
        </div>
      ) : loading ? (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, padding: 40, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("workforce.mySchedule.loading", "Loading your schedule…")}</div>
      ) : mode === "month" ? (
        <MonthGrid anchor={anchor} roster={roster} timeOff={timeOff} todayIso={todayIso}
          onOpenShift={(iso, s) => setEdit({ date: iso, dates: [iso], shift: s, draft: s ? undefined : { start: "09:00", end: "17:00" } })} />
      ) : mode === "week" ? (
        <TimeGrid dates={dates} days={days} timeOff={timeOff} lo={lo} hi={hi} todayIso={todayIso} nowMin={nowMin} showAppts={showAppts} avail={avail}
          onEditShift={(d, s) => setEdit({ date: d, dates: [d], shift: s })}
          onCreateShift={(ds, s, e) => setEdit({ date: ds[0], dates: ds, shift: null, draft: { start: s, end: e } })} />      ) : (
        <DayBoard date={anchor} day={dayData} timeOff={timeOff} avail={avail}
          lo={lo} hi={hi} isToday={anchor === todayIso} nowMin={nowMin} busy={busy}
          sel={sel} setSel={setSel} showAppts={showAppts}
          onSwap={() => { const id = dayData?.shift?.id; if (id) doSwap(id); }}
          onTimeOff={() => onRequestTimeOff(anchor)}
          onReportSick={() => onReportSick(anchor)}
          onEditShift={() => { const s = dayData?.shift; if (s) setEdit({ date: anchor, dates: [anchor], shift: s }); }}
          onCreateShift={(s, e) => setEdit({ date: anchor, dates: [anchor], shift: null, draft: { start: s, end: e } })}
          onBlockRange={(s, e) => { const sh = dayData?.shift; if (sh) setEdit({ date: anchor, dates: [anchor], shift: sh, newBreak: { start: s, end: e } }); }} />
      )}

      {mode !== "month" && !loading && !error ? (
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", fontSize: 11.5, color: T.soft }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: alpha(T.p1, 14), border: `1px solid ${alpha(T.p1, 30)}`, borderLeft: `3px solid ${T.p1}` }} />{t("workforce.mySchedule.legendShiftRoster", "my rostered hours")}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: 2, background: HATCH, border: `1px dashed ${T.n5}` }} />{t("workforce.mySchedule.breaks", "break / lunch")}</span>
          {shownWindows.length ? (
            <span style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="mysched-avail-key">
              <span>{t("workforce.mySchedule.legendOffering", "colour = what you offer")}</span>
              {shownWindows.map((w) => (
                <span key={w.name} style={{ display: "flex", alignItems: "center", gap: 6, color: T.n2 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: alpha(w.hex, 22), borderLeft: `3px solid ${w.hex}` }} />{w.name}
                </span>
              ))}
            </span>
          ) : null}
          {mode === "day" ? <span style={{ marginLeft: "auto" }}>{t("workforce.mySchedule.dayHint", "Drag down the time axis to select a range — snaps to 15 minutes.")}</span> : null}
        </div>
      ) : null}

      {edit ? <ShiftEditDrawer date={edit.date} dates={edit.dates} weekDays={weekDayStates} shift={edit.shift} draft={edit.draft} newBreak={edit.newBreak}
        template={template} busy={busy} run={run}
        avail={avail} savingAvail={savingAvail} onSaveAvail={saveAvail}
        onSaved={() => { setEdit(null); onReload(); }} onClose={() => setEdit(null)} /> : null}
    </div>
  );
}
